// An in-memory api for the console's tests: the routes the first screens call, answering in the
// contract's shapes and with its rules — ETags and `If-Match` (spec/08 §2.5), problem+json and
// better-auth's `{ code, message }` (§2.6), the console gate (spec/07 §6.6), locks (spec/03 §9) — and
// the HQ console's: the platform role at `GET /v1/me/access`, the HQ guard on `/v1/hq/*` (a platform
// role, a second factor, its permission), HQ's settings screens with their locks, and the brand —
// and HQ's Players and the platform audit log, in their own file (fake-hq-players-audit.ts).
//
// The component tests hand the generated client a `fetch` built on it; the accessibility test routes
// the built console's requests to it through Playwright. The settings' JSON Schemas are made by the
// registry's own `describeSetting`, so the renderer is tested against what the server really sends.

import type {
  AuthSessionState,
  File as FileResource,
  FloorStationTitle,
  StationClient,
  StationClientChannel,
  HqBrandView,
  HqLive,
  PassProduct,
  Problem,
  SettingEntry,
  SettingsView,
  Venue,
} from '@clavian/api-client';
import {
  BRAND_RUNTIME_GROUPS,
  BrandRuntimeChangeSchema,
  CreatePassProductRequestSchema,
  defineSetting,
  describeSetting,
  ORGANIZATION_ROLE_GRANTS,
  ORGANIZATION_ROLES,
  OtpChannelSettingSchema,
  permissionsForPlatformRole,
  MemberLimitsSchema,
  RoleLimitsSchema,
  UpdateOrganizationBusinessDetailsSchema,
  VenueClosingActionSchema,
  DayCloseModeSchema,
  CashHandlingSchema,
  VenuePowerScheduleSchema,
  settingUnit,
  z,
  type AnySettingDefinition,
  type BrandRuntimeValues,
} from '@clavian/contracts';

import type { MyAccessResponse, MyOrganizationResponse, MyVenueResponse } from '../api/me';
import { createFakeAccount } from './fake-account';
import { createFakeHqCatalogue } from './fake-hq-catalogue';
import { createFakeHqDirectory, NITRO, NITRO_VENUE_ID } from './fake-hq-directory';
import { createHqPlayersAudit } from './fake-hq-players-audit';
import { createFakeHqLeads } from './fake-hq-leads';
import { createFakeHqMigrations, movedPlayerFixtures } from './fake-hq-migrations';
import { createFakeHqAddons } from './fake-hq-addons';
import { createFakeHqPayments } from './fake-hq-payments';
import { createFakeHqProviders } from './fake-hq-providers';
import { createFakeHqReleases } from './fake-hq-releases';
import { createFakeCommerce } from './fake-venue-commerce';
import { createFakeGuests } from './fake-venue-guests';
import { createFakeBoot, type BootFakeMode } from './fake-venue-boot';
import { createFakeFloor } from './fake-venue-floor';
import { createFakeOps } from './fake-venue-ops';
import { createFakePromotions } from './fake-org-promotions';
import { createFakeCredit } from './fake-venue-credit';
import { createFakeOrgVenues } from './fake-org-venues';
import { createFakePayments } from './fake-venue-payments';
import { COUNTER_STATION_ID, createFakePrinting, type PrintingOptions } from './fake-venue-printing';
import { createFakePlayers } from './fake-venue-players';
import { createFakeTax } from './fake-venue-tax';
import { createFakeRateCard } from './fake-venue-rate-card';
import { fakeDailySummaryResponse } from './fake-daily-summary';
import { fakeReportsResponse } from './fake-venue-reports';
import { createFakeInventory, type InventoryOptions } from './fake-venue-inventory';
import { applyOrganizationSkip, applySkip, type FakeSetupState, goLiveMissing, hqSetupOf, organizationSetupOf, type SetupFacts, venueSetupOf } from './fake-setup';

export const ORGANIZATION = { id: 'org_01k5znw4hmfay8vvr14d2pf2db', slug: 'playground', name: 'Playground Gaming' };

/** HQ Live's read model in the fake (F-ANL-02): one venue busy, one offline for 25 minutes in its opening hours. */
const HQ_LIVE: HqLive = {
  computedAt: '2026-09-30T09:00:00.000Z',
  playersInSession: 18,
  stationsInUse: 18,
  stationsTotal: 64,
  venuesOnline: 3,
  venuesTotal: 4,
  organizationsOnline: 2,
  organizationsTotal: 2,
  takingsToday: [{ amount: 1_254_000, currency: 'INR' }],
  newPlayersToday: 5,
  offlineVenues: [
    {
      organizationId: 'org_01k5znw4hmfay8vvr14d2pf2dc',
      venueId: 'ven_01k5znw4hmfay8vvr14d2pf2dd',
      name: 'Level Up Koramangala',
      sessionsNow: 0,
      stationsTotal: 12,
      stationsOnline: 0,
      offlineSince: '2026-09-30T08:35:00.000Z',
    },
  ],
  busiestVenues: [
    {
      organizationId: 'org_01k5znw4hmfay8vvr14d2pf2db',
      venueId: 'ven_01k5znw4hmfay8vvr14d2pf2de',
      name: 'Playground Indiranagar',
      sessionsNow: 14,
      stationsTotal: 24,
      stationsOnline: 22,
      offlineSince: null,
    },
  ],
};
export const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
export const SECOND_VENUE = { id: 'ven_01k5znw4hmfay8vvr14d2pf2dd', slug: 'playground-north', name: 'Playground North' };

/**
 * A second organisation the member works for, with three venues (`otherOrganization`): the venue switcher's groups
 * (spec/13 §2). Listed before Playground Gaming, as the server lists organisations by name.
 */
export const OTHER_ORGANIZATION = { id: 'org_01k5znw4hmfay8vvr14d2pf3aa', slug: 'arcadia', name: 'Arcadia Esports' };
export const OTHER_VENUES = [
  { id: 'ven_01k5znw4hmfay8vvr14d2pf3a1', slug: 'arcadia-hsr-layout', name: 'Arcadia HSR Layout' },
  { id: 'ven_01k5znw4hmfay8vvr14d2pf3a2', slug: 'arcadia-indiranagar', name: 'Arcadia Indiranagar' },
  { id: 'ven_01k5znw4hmfay8vvr14d2pf3a3', slug: 'arcadia-koramangala', name: 'Arcadia Koramangala' },
] as const;

/** One organisation player, for the Players screen and a player's Audit tab (spec/13 §8). */
export const ORG_PLAYER = {
  id: 'ply_01k5znw4hmfay8vvr14d2pf2a1',
  username: null as string | null,
  displayName: null as string | null,
  firstName: 'Aarav',
  lastName: 'Mehta',
  phoneNumber: '•••210',
  email: 'a•••@example.com',
  contactMasked: true,
  tags: ['regular'],
  knownSince: '2026-01-05T10:00:00.000Z',
};
/** The organisation's staff, as `GET /v1/organizations/{id}/members` returns them (masked contacts). */
export const STAFF_MEMBERS = [
  { id: 'mem_01k5znw4hmfay8vvr14d2pf2e1', organizationId: ORGANIZATION.id, role: 'owner', venueIds: [], status: 'active', suspendedAt: null, name: 'Riya Sharma', phoneNumber: null, email: 'r•••@playground.example', secondFactorEnabled: true, joinedAt: '2026-03-02T09:00:00Z' },
  { id: 'mem_01k5znw4hmfay8vvr14d2pf2e2', organizationId: ORGANIZATION.id, role: 'cashier', venueIds: [VENUE_ID], status: 'active', suspendedAt: null, name: 'Kabir Mehta', phoneNumber: '+91•••••43210', email: null, secondFactorEnabled: false, joinedAt: '2026-06-14T12:30:00Z' },
  { id: 'mem_01k5znw4hmfay8vvr14d2pf2e3', organizationId: ORGANIZATION.id, role: 'floor', venueIds: [VENUE_ID], status: 'suspended', suspendedAt: '2026-09-01T18:00:00Z', name: null, phoneNumber: '+91•••••11223', email: null, secondFactorEnabled: false, joinedAt: '2026-07-20T15:00:00Z' },
] as const;

/** The invitations out, as `GET /v1/organizations/{id}/invitations` returns them. */
export const STAFF_INVITATIONS = [
  { id: 'inv_01k5znw4hmfay8vvr14d2pf2e4', organizationId: ORGANIZATION.id, channel: 'email', destination: 'a•••@playground.example', role: 'manager', venueIds: [], status: 'pending', expiresAt: '2026-10-03T09:00:00Z', createdAt: '2026-09-26T09:00:00Z' },
] as const;

export const TOTP_CODE = '654321';
export const PHONE_CODE = '123456';
export const PASSWORD = 'correct horse battery';
/** `POST /v1/auth/email-otp/verify-email`'s code (F-CMP-06, spec/07 §2.1 `emailOTP`). */
export const EMAIL_OTP_CODE = '246810';
/** A breached password `PASSWORD_COMPROMISED` refuses (`haveIBeenPwned`, spec/07 §2.1). */
export const BREACHED_PASSWORD = 'password123';
/** The one pairing code the fake api approves (F-STN-02, spec/10 §3.2): 8 characters, as the PC shows. */
export const PAIRING_CODE = 'PAIR2345';
/** The one seeded station (F-ORG-03): unpaired (offline) by default, online and free with `stationsOnline`. */
export const STATION_ID = 'stn_01k5znw4hmfay8vvr14d2pf3cc';
/** The device the fake api binds to a station when a pairing is approved (`dev_…`, spec/07 §5). */
export const DEVICE_ID = 'dev_01k5znw4hmfay8vvr14d2pf3ee';

/** `POST /v1/invitations/accept`'s outcomes, chosen by the token sent (spec/07 §6.5). Any other token
 * answers `not_found`, as a forged or mistyped one does. */
export const INVITATION_TOKENS = {
  valid: 'inv-token-valid',
  unavailable: 'inv-token-unavailable',
  contactMismatch: 'inv-token-contact-mismatch',
  alreadyMember: 'inv-token-already-member',
  secondFactor: 'inv-token-second-factor',
  sharedDevice: 'inv-token-shared-device',
  /** A café's first owner, invited by HQ by email (ADR-0046). */
  owner: 'inv-token-owner',
  /** A café's first owner, invited by HQ by phone, to a number with no account yet. */
  ownerPhone: 'inv-token-owner-phone',
} as const;

/** The number an HQ phone invitation is for: no account uses it, so verifying it asks for the terms first. */
export const NEW_OWNER_PHONE = '+919812345678';

/** When the fake's invitations stop working: years ahead, so a browser keeps their token whatever today is. */
const PREVIEW_EXPIRES = '2036-09-29T09:00:00.000Z';

/**
 * What `POST /v1/invitations/preview` answers for each token (ADR-0046): who invited whom to what, until when, while it
 * is pending; its status alone once it is not.
 */
export const INVITATION_PREVIEWS: Readonly<Record<string, Record<string, unknown>>> = {
  'inv-token-valid': {
    organizationName: 'Playground Gaming',
    role: 'cashier',
    invitedBy: 'member',
    inviterName: 'Riya Sharma',
    channel: 'phone',
    destination: '•••210',
    contact: { phoneNumber: '+919876543210' },
    status: 'pending',
    expiresAt: PREVIEW_EXPIRES,
  },
  // Used, withdrawn or expired: its status, and nothing else.
  'inv-token-unavailable': { status: 'revoked' },
  'inv-token-contact-mismatch': {
    organizationName: 'Playground Gaming',
    role: 'manager',
    invitedBy: 'member',
    inviterName: 'Riya Sharma',
    channel: 'email',
    destination: 'n•••@newvenue.example',
    contact: { email: 'nikhil@newvenue.example' },
    status: 'pending',
    expiresAt: PREVIEW_EXPIRES,
  },
  'inv-token-already-member': {
    organizationName: 'Playground Gaming',
    role: 'cashier',
    invitedBy: 'member',
    inviterName: null,
    channel: 'phone',
    destination: '•••210',
    contact: { phoneNumber: '+919876543210' },
    status: 'pending',
    expiresAt: PREVIEW_EXPIRES,
  },
  'inv-token-second-factor': {
    organizationName: 'Level Up Lounge',
    role: 'owner',
    invitedBy: 'hq',
    inviterName: null,
    channel: 'email',
    destination: 'r•••@playground.example',
    contact: { email: 'riya@playground.example' },
    status: 'pending',
    expiresAt: PREVIEW_EXPIRES,
  },
  'inv-token-shared-device': {
    organizationName: 'Playground Gaming',
    role: 'cashier',
    invitedBy: 'member',
    inviterName: 'Riya Sharma',
    channel: 'phone',
    destination: '•••210',
    contact: { phoneNumber: '+919876543210' },
    status: 'pending',
    expiresAt: PREVIEW_EXPIRES,
  },
  'inv-token-owner': {
    organizationName: 'Level Up Lounge',
    role: 'owner',
    invitedBy: 'hq',
    inviterName: null,
    channel: 'email',
    destination: 'a•••@levelup.example',
    contact: { email: 'anika@levelup.example' },
    status: 'pending',
    expiresAt: PREVIEW_EXPIRES,
  },
  'inv-token-owner-phone': {
    organizationName: 'Level Up Lounge',
    role: 'owner',
    invitedBy: 'hq',
    inviterName: null,
    channel: 'phone',
    destination: '•••678',
    contact: { phoneNumber: '+919812345678' },
    status: 'pending',
    expiresAt: PREVIEW_EXPIRES,
  },
};

/** `GET /v1/meta`'s `brand.legal` (F-CMP-06, spec/03 §12.4): the sign-up screen's terms and privacy. */
export const LEGAL = {
  terms: { version: '2026-01-01', url: 'https://clavian.test/legal/terms' },
  privacy: { version: '2026-01-01', url: 'https://clavian.test/legal/privacy' },
};

const NOW = '2026-09-22T09:00:00.000Z';
const REQUEST_ID = 'req_01k5znw4hmfay8vvr14d2pf2db';

/** Which status changes a venue edit may make, as the server's own table: nothing goes back to `setup`. */
const VENUE_TRANSITIONS: Readonly<Record<string, readonly string[]>> = { setup: ['live'], live: ['paused'], paused: ['live'] };

export function venueFixture(overrides: Partial<Venue> = {}): Venue {
  return {
    id: VENUE_ID,
    organizationId: ORGANIZATION.id,
    name: 'Playground Central',
    slug: 'playground-central',
    timezone: 'Asia/Kolkata',
    businessDayCutoff: '06:00',
    businessDayCutoffHistory: [],
    currency: 'INR',
    address: { line1: '12 MG Road', locality: 'Bengaluru', region: 'Karnataka', postalCode: '560001', country: 'IN' },
    coordinate: null,
    contact: { phone: '+919876543210' },
    openingHours: { mon: [{ opens: '10:00', closes: '23:00' }], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] },
    holidays: [],
    amenities: ['parking'],
    photos: { cover: null, gallery: [] },
    logo: null,
    status: 'live',
    publicListing: true,
    featured: false,
    partner: false,
    sponsored: false,
    createdAt: NOW,
    updatedAt: NOW,
    archivedAt: null,
    ...overrides,
  };
}

// ---------------------------------------------------------------------------------------------
// The registry, as the server describes it
// ---------------------------------------------------------------------------------------------

const definitions: Record<string, AnySettingDefinition> = {
  'venue.closingAction': defineSetting({
    key: 'venue.closingAction',
    type: VenueClosingActionSchema,
    default: 'warn_only',
    scopes: ['venue'],
    visibleTo: 'venue',
    group: 'venueProfile',
    lockableAt: [],
    editPermission: 'venues:update',
    entitlement: null,
    description: 'At closing time, warn players 15 and 5 minutes before and keep their sessions running, or warn and then end them.',
  }),
  'venue.powerSchedule': defineSetting({
    key: 'venue.powerSchedule',
    type: VenuePowerScheduleSchema,
    default: { mode: 'off' },
    scopes: ['venue'],
    visibleTo: 'venue',
    group: 'stationsAndLocker',
    lockableAt: [],
    editPermission: 'settings:locker',
    entitlement: null,
    description: 'Wake every station before opening time and shut down idle, locked ones after closing — never during a session.',
  }),
  // Session hygiene (F-LCK-07, apps/server/src/modules/stations/settings.ts): how deep the between-players wipe
  // goes. The console shows a "not recommended" callout when it is set to `off` (spec/11 §5). Scoped venue-only in
  // this fixture (the real setting also takes org/platform/class) so it exercises the venue screen's callout
  // without changing the organisation screen's group list the other tests assert.
  'cleanup.profile': defineSetting({
    key: 'cleanup.profile',
    type: z.enum(['light', 'full', 'off']),
    default: 'light',
    scopes: ['venue'],
    visibleTo: 'venue',
    group: 'stationsAndLocker',
    lockableAt: [],
    editPermission: 'settings:locker',
    entitlement: null,
    description:
      'How much a station clears between two players. Light (recommended) signs out browsers and game launchers and clears downloads, temp files, recent files and the clipboard. Full also wipes the Desktop, Documents, Pictures, Videos and Music, keeping your game-save folders. Off clears nothing extra — only for venues that reset the disk another way.',
  }),
  // Days and shifts (ADR-0029; apps/server/src/modules/shifts/settings.ts): how the day ends, and whether drawers are
  // counted per shift. The ops fake reads both for `GET …/business-day` and the day close.
  'shifts.dayCloseMode': defineSetting({
    key: 'shifts.dayCloseMode',
    type: DayCloseModeSchema,
    default: 'manual',
    scopes: ['venue'],
    visibleTo: 'venue',
    group: 'daysAndShifts',
    lockableAt: [],
    editPermission: 'settings:payments',
    entitlement: null,
    description:
      'How the business day ends. Manual: a manager closes the day once every shift is counted. Automatic: the day rolls over at the cutoff by itself and its end-of-day report is ready for a manager to sign off — recommended for venues open 24×7, with the cutoff at the quietest hour.',
  }),
  'shifts.cashHandling': defineSetting({
    key: 'shifts.cashHandling',
    type: CashHandlingSchema,
    default: 'per_drawer',
    scopes: ['organization', 'venue'],
    visibleTo: 'venue',
    group: 'daysAndShifts',
    lockableAt: [],
    editPermission: 'settings:payments',
    entitlement: null,
    description:
      'How cash is handled when two or more people work at once. Per drawer: each counter has its own drawer and shift. Shared drawer: one drawer, one shift, and colleagues join it. Floor without a drawer: people without a drawer take card, UPI and wallet; cash goes to someone with an open drawer.',
  }),
  'shifts.enabled': defineSetting({
    key: 'shifts.enabled',
    type: z.boolean(),
    default: true,
    scopes: ['organization', 'venue'],
    visibleTo: 'venue',
    group: 'daysAndShifts',
    lockableAt: [],
    editPermission: 'settings:payments',
    entitlement: null,
    description:
      'Whether staff count the cash drawer per shift: start a shift with the float, hand over with a count, end with a final count. Turn off if the venue does not track drawers; nobody is then asked to start a shift.',
  }),
  // A lockable setting, locked by HQ at platform scope — none is registered yet (spec/03 §9), so the
  // lock is exercised with one the tests define.
  'sessions.graceMinutes': defineSetting({
    key: 'sessions.graceMinutes',
    type: z.int().min(0).max(30),
    default: 5,
    scopes: ['platform', 'organization', 'venue'],
    visibleTo: 'venue',
    group: 'sessions',
    lockableAt: ['platform'],
    editPermission: 'settings:sessions',
    entitlement: null,
    description: 'Minutes a player keeps playing after their time runs out, before the station locks.',
  }),
  // Pay-as-you-go pricing (apps/server/src/modules/sessions/settings.ts), drawn as a card on the
  // Pricing & promotions screen (spec/13 §18) and edited there with the same renderer.
  'sessions.paygHourlyRate': defineSetting({
    key: 'sessions.paygHourlyRate',
    type: z.int().min(0),
    default: 6000,
    scopes: ['platform', 'organization', 'venue'],
    visibleTo: 'venue',
    group: 'sessions',
    lockableAt: [],
    editPermission: 'settings:sessions',
    entitlement: null,
    unit: 'money',
    description: 'The pay-as-you-go price for an hour of play.',
  }),
  'sessions.paygBillingUnitSeconds': defineSetting({
    key: 'sessions.paygBillingUnitSeconds',
    type: z.union([z.literal(60), z.literal(300), z.literal(900), z.literal(1800)]),
    default: 60,
    scopes: ['platform', 'organization', 'venue'],
    visibleTo: 'venue',
    group: 'sessions',
    lockableAt: [],
    editPermission: 'settings:sessions',
    entitlement: null,
    description: 'The unit pay-as-you-go play is billed in: a started unit is charged in full. 60, 300, 900 or 1800 seconds.',
  }),
  'sessions.paygDefaultBudget': defineSetting({
    key: 'sessions.paygDefaultBudget',
    type: z.int().min(0),
    default: 0,
    scopes: ['platform', 'organization', 'venue'],
    visibleTo: 'venue',
    group: 'sessions',
    lockableAt: [],
    editPermission: 'settings:sessions',
    entitlement: null,
    unit: 'money',
    description: 'How much a player who signs in at the kiosk without choosing how to pay may spend on their account. Zero asks them to add time or money first.',
  }),
  'sessions.walletFundsPlay': defineSetting({
    key: 'sessions.walletFundsPlay',
    type: z.boolean(),
    default: false,
    scopes: ['platform', 'organization', 'venue'],
    visibleTo: 'venue',
    group: 'sessions',
    lockableAt: [],
    editPermission: 'settings:sessions',
    entitlement: null,
    description: "Whether a player's wallet pays for play at the pay-as-you-go rate, after their passes and before anything goes on account.",
  }),
  // A value the renderer has no control for — a union of a text and a number — which it edits as JSON.
  'kiosk.attractInterval': defineSetting({
    key: 'kiosk.attractInterval',
    type: z.union([z.string().min(1), z.int().min(5)]),
    default: 30,
    scopes: ['venue'],
    visibleTo: 'venue',
    group: 'kiosk',
    lockableAt: [],
    editPermission: 'settings:kiosk',
    entitlement: null,
    description: 'Seconds between attract slides, or a named schedule.',
  }),
  // F-KIO-04 (apps/server/src/modules/identity/settings.ts): whether the kiosk offers "Create account".
  'kiosk.allowSignUp': defineSetting({
    key: 'kiosk.allowSignUp',
    type: z.boolean(),
    default: true,
    scopes: ['venue'],
    visibleTo: 'venue',
    group: 'kiosk',
    lockableAt: [],
    editPermission: 'settings:kiosk',
    entitlement: null,
    description:
      'Whether new players can create an account at the PCs. Off, the kiosk hides “Create account” and new accounts are made at the front desk or in the app; players who already have one sign in as usual.',
  }),
  'staff.require2faRoles': defineSetting({
    key: 'staff.require2faRoles',
    type: z.array(z.string().min(1).max(64)).max(40),
    default: ['owner', 'manager', 'accountant'],
    scopes: ['platform', 'organization'],
    visibleTo: 'organization',
    group: 'staff',
    lockableAt: [],
    editPermission: 'settings:all',
    entitlement: null,
    description: 'Organisation roles that must sign in with a passkey or a second factor before the console will answer.',
  }),
  'auth.otpChannel': defineSetting({
    key: 'auth.otpChannel',
    type: OtpChannelSettingSchema,
    default: 'sms',
    scopes: ['platform', 'organization'],
    visibleTo: 'organization',
    group: 'notifications',
    lockableAt: [],
    editPermission: 'settings:notifications',
    entitlement: null,
    description: 'How sign-in codes reach a phone: SMS, WhatsApp, or both. SMS is always the fallback.',
  }),
  // Money settings (`unit: 'money'`, spec/03 §9): integer paise on the wire, rupees on the screen —
  // one whose whole value is the amount, and a list whose items carry two.
  // The platform's commission on online payments (F-PAY-06): head office's to set, the organisation's to see.
  'payments.commissionBps': defineSetting({
    key: 'payments.commissionBps',
    type: z.int().min(0).max(5000),
    default: 0,
    scopes: ['platform', 'organization'],
    visibleTo: 'organization',
    group: 'payments',
    lockableAt: [],
    editPermission: 'hq:settings',
    entitlement: null,
    description: 'The commission on each online payment, in basis points (100 = 1%). Set by the platform.',
  }),
  'wallet.maxBalanceAmount': defineSetting({
    key: 'wallet.maxBalanceAmount',
    type: z.int().min(0),
    default: 2_000_000,
    scopes: ['platform', 'organization'],
    visibleTo: 'venue',
    group: 'payments',
    lockableAt: ['platform'],
    editPermission: 'settings:payments',
    entitlement: null,
    unit: 'money',
    description: 'The most a wallet may hold, cash and bonus together. Head office may lock it.',
  }),
  // The kiosk's top-up limits, amounts and extras (F-WAL-03, F-KIO-10; 2026-10-01), as the wallet module declares them:
  // the limits set at the organisation and visible to a venue (read-only there), the amounts and extras venue-scoped,
  // each extras/amounts list drawn by its own control (`x-clavian-control`).
  'wallet.minTopupAmount': defineSetting({
    key: 'wallet.minTopupAmount',
    type: z.int().min(0),
    default: 10_000,
    scopes: ['platform', 'organization'],
    visibleTo: 'venue',
    group: 'payments',
    lockableAt: ['platform'],
    editPermission: 'settings:payments',
    entitlement: null,
    unit: 'money',
    description: 'The smallest wallet top-up.',
  }),
  'wallet.maxTopupAmount': defineSetting({
    key: 'wallet.maxTopupAmount',
    type: z.int().min(0),
    default: 1_000_000,
    scopes: ['platform', 'organization'],
    visibleTo: 'venue',
    group: 'payments',
    lockableAt: ['platform'],
    editPermission: 'settings:payments',
    entitlement: null,
    unit: 'money',
    description: 'The largest single wallet top-up. Head office may lock it.',
  }),
  'wallet.topupOffers': defineSetting({
    key: 'wallet.topupOffers',
    type: z
      .array(
        z.object({
          id: z.string().min(1).max(64),
          minTopupAmount: settingUnit(z.int().min(0), 'money'),
          bonusAmount: settingUnit(z.int().min(0), 'money'),
        }),
      )
      .max(20)
      .meta({ 'x-clavian-control': 'topupOffers' }),
    default: [],
    scopes: ['organization', 'venue'],
    visibleTo: 'venue',
    group: 'payments',
    lockableAt: [],
    editPermission: 'settings:payments',
    entitlement: null,
    description: 'Top-up extras — add at least an amount, get a bonus on top. A kiosk top-up earns the best tier it reaches; a venue’s own list replaces the organisation’s there. Empty by default.',
  }),
  'wallet.topupPresets': defineSetting({
    key: 'wallet.topupPresets',
    type: z
      .array(settingUnit(z.int().min(1), 'money'))
      .max(12)
      .meta({ 'x-clavian-control': 'topupPresets' }),
    default: [],
    scopes: ['organization', 'venue'],
    visibleTo: 'venue',
    group: 'payments',
    lockableAt: [],
    editPermission: 'settings:payments',
    entitlement: null,
    description: 'The amounts the kiosk offers to top up, each within the smallest and largest top-up. Empty: the kiosk offers the smallest top-up and each extra’s threshold, as before. Players can always type another amount.',
  }),
  // Lockable at platform **and** organisation scope, and on HQ's screens only (`visibleTo: 'platform'`),
  // so HQ's organisation screen has a lock of its own to set while the staff screens stay as they are.
  'support.notifyPlayerOnView': defineSetting({
    key: 'support.notifyPlayerOnView',
    type: z.boolean(),
    default: true,
    scopes: ['platform', 'organization', 'venue'],
    visibleTo: 'platform',
    group: 'players',
    lockableAt: ['platform', 'organization'],
    editPermission: 'settings:players',
    entitlement: null,
    description: 'Tell a player when support views their account.',
  }),
};

/** The seeded station class (`Standard PC`): the one whose own values `classSettings` stores. */
export const CLASS_ID = 'scl_01k5znw4hmfay8vvr14d2pf3aa';

/** The one organisation HQ's fake knows; any other id is `404`. */
const HQ_ORGANIZATION_SETTINGS = `/v1/hq/organizations/${ORGANIZATION.id}/settings`;

/** The deployment's brand values, for HQ's brand screen: the Clavian default's, as the file ships. */
const DEPLOYMENT_BRAND: HqBrandView['deployment'] = {
  support: { email: 'hello@clavian.io' },
  theme: {},
  kioskScreensaver: { media: [] },
  organizationBranding: { colours: true, logo: true },
};

interface StoredValue {
  value: unknown;
  locked?: boolean;
}

const GROUP_TITLES: Record<string, string> = {
  venueProfile: 'Venue profile',
  kiosk: 'Kiosk',
  sessions: 'Sessions',
  offlinePlay: 'Offline play',
  stationsAndLocker: 'Stations and locker',
  payments: 'Payments',
  daysAndShifts: 'Days and shifts',
  staff: 'Staff and roles',
  notifications: 'Notifications',
  players: 'Players',
};

// ---------------------------------------------------------------------------------------------
// The server
// ---------------------------------------------------------------------------------------------

export interface FakeServerOptions {
  /** Signed in at the start. */
  readonly signedIn?: boolean;
  /** What `GET /v1/me/access` grants; `'unavailable'` answers 404, as before F-IAM-11 lands. */
  readonly permissions?: readonly string[] | 'unavailable';
  /** What the server itself allows — the enforcement, which a stale `/me/access` does not change. */
  readonly serverPermissions?: readonly string[];
  readonly venue?: Partial<Venue>;
  /** The organisation's name at `GET /v1/me/organizations`, when not `ORGANIZATION.name` (the public demo's, spec/24). */
  readonly organizationName?: string;
  readonly twoFactorOnPassword?: boolean;
  /** Console routes answer `second_factor_required` until the session proves one. */
  readonly secondFactorRequired?: boolean;
  readonly twoFactorEnabled?: boolean;
  readonly venues?: 'one' | 'two';
  /**
   * The signed-in person's platform role (spec/07 §7): what `GET /v1/me/access` answers at platform
   * scope and what the HQ guard checks. `null` (the default) is a person with none.
   */
  readonly platformRole?: string | null;
  /** The signed-in member's organisation role at `GET /v1/me/access`; by default `manager` or `cashier` from the permissions. */
  readonly organizationRole?: string;
  /** The member's staff access covers only some venues (`allVenues: false` at `GET /v1/me/organizations`). */
  readonly venueOnly?: boolean;
  /** The person works at no venue: `GET /v1/me/venues` is empty. */
  readonly noVenues?: boolean;
  /**
   * The member works for a second organisation too (`OTHER_ORGANIZATION`, three venues), at every venue (`'every-venue'`:
   * its own area is offered) or some (`'some-venues'`). Absent: Playground Gaming alone.
   */
  readonly otherOrganization?: 'every-venue' | 'some-venues';
  /** Signed in at the start with a session that has already proved a second factor. */
  readonly sessionSecondFactor?: boolean;
  /**
   * What the HQ guard allows at platform scope, when it differs from what `GET /v1/me/access` said the
   * role holds (a role changed since the console read it). Defaults to the role's own permissions.
   */
  readonly platformServerPermissions?: readonly string[];
  /** What `GET /v1/me/access` reports at platform scope, when it is to differ from the role's own bundle (a role narrowed for a test). */
  readonly platformAccessPermissions?: readonly string[];
  /** `'unavailable'` omits `GET /v1/meta`'s `brand.legal`, as a brand that names neither document does. */
  readonly legal?: 'unavailable';
  /** The social providers `GET /v1/meta` says are switched on (F-IAM-03 … F-IAM-05). None by default. */
  readonly socialProviders?: readonly ('google' | 'discord' | 'steam')[];
  /**
   * The signed-in person's ways in (`GET /v1/auth/list-accounts`), as `providerId`s with when each was linked.
   * Default: their password alone.
   */
  readonly linkedAccounts?: readonly { readonly providerId: 'credential' | 'google' | 'discord' | 'steam'; readonly createdAt?: string }[];
  /** The session was signed in more than ten minutes ago: unlinking answers `SESSION_NOT_FRESH`. */
  readonly staleSession?: boolean;
  /** `false`: a staff member's first sign-in with a provider, the profile still to complete. Default true. */
  readonly profileComplete?: boolean;
  /**
   * `auth.phoneSignIn`, as `GET /v1/meta`'s `signIn.phone` says it and the phone endpoints enforce it. On by
   * default, because most of these tests sign in and invite by phone; a test of the switch passes `false` — the
   * Platform's own default since 2026-10-08.
   */
  readonly phoneSignIn?: boolean;
  /**
   * The organisation's business details (ADR-0046): `complete` (the default) with its legal name and registered
   * address, or `incomplete` — a café just invited, whose owner has not added them yet.
   */
  readonly businessDetails?: 'complete' | 'incomplete';
  /** The catalogue's metadata provider is not configured: search and mirror answer `503` (spec/14 §6). */
  readonly catalogueProviderUnavailable?: boolean;
  /** Players moved from a previous system wait to be claimed: the Players page shows its "Moved" tab (F-HQ-40). */
  readonly movedPlayers?: boolean;
  /** The tax mode a move's venue has on its switch-over day (F-HQ-40): `regular` by default. */
  readonly migrationVenueTaxMode?: 'regular' | 'composition' | 'unregistered';
  /** The sales pipeline starts with no lead and no template: the feature's first day. */
  readonly emptyLeads?: boolean;
  /** The platform catalogue starts empty: `GET /v1/hq/catalogue/games` returns no rows. */
  readonly emptyCatalogue?: boolean;
  /** SteamGridDB is not configured: hero candidates come from IGDB alone and logos answer `503` (ADR-0028). */
  readonly steamGridDbUnconfigured?: boolean;
  /** A lived-in catalogue for screenshots and demos: more games (with and without art) and a dozen apps, some with icons. */
  readonly catalogueShowcase?: boolean;
  /** The seeded station is paired at the start, so the live floor reads it online and free (F-STN-14). */
  readonly stationsOnline?: boolean;
  /**
   * The seeded station, online, is in its start-up setup (ADR-0055): the floor reads it `setting_up` with this progress
   * (or with none, `null`, as a station that has not said yet). Needs `stationsOnline`.
   */
  readonly stationSetup?: { readonly total: number; readonly done: number; readonly skipped: number; readonly current: string | null } | null;
  /**
   * The seeded station's client row at `GET …/station-clients` (F-STN-12): by default it runs 1.9.41000.0 and is
   * offered 1.9.41234.0 ("Update available"); `null` leaves it out (never reported). Fields given override the default.
   */
  readonly stationClient?: Partial<Omit<StationClient, 'stationId'>> | null;
  /** The venue's effective `station.clientChannel` on that list. `stable` by default. */
  readonly stationClientChannel?: StationClientChannel;
  /** The installers at `GET …/station-installers` (F-STN-17): both MSIs (default), the Station MSI only, or none published. */
  readonly stationInstallers?: 'both' | 'station-only' | 'no-setup' | 'none';
  /** Station releases start empty: nothing registered, no rollout, no station reporting (spec/14 §12). */
  readonly emptyStationReleases?: boolean;
  /** What the seeded station is running now on the floor snapshot (F-CAT-04), while online. None by default. */
  readonly stationTitle?: FloorStationTitle | null;
  /**
   * A venue in full swing, for the screens that need one to render at all: the live floor and its
   * sessions, players and wallets, shifts, passes and the library (fake-venue-*.ts). Off by default,
   * so the Stations and settings tests keep their one seeded station.
   */
  readonly liveVenue?: boolean;
  /**
   * Guest passes (fake-venue-guests.ts, F-SES-07): `true` — the venue serves guests (`sessions.guestsEnabled`);
   * `'seeded'` — and already has guest passes in use. Absent, guests are off, as on every venue by default.
   */
  readonly guests?: boolean | 'seeded';
  /**
   * Clavian Boot (fake-venue-boot.ts, F-BOOT-01 …): `true` — the organisation has the add-on and the venue a boot server,
   * images and PCs; `'empty'` — the add-on, nothing set up yet; `'unentitled'` — no add-on, every boot route `402`.
   * Absent, the boot routes are not answered.
   */
  readonly boot?: boolean | 'empty' | 'unentitled';
  /**
   * Which Clavian products the organisation has, as `GET /v1/me/access` says (ADR-0065). `{ stations: false, boot: true }` is a
   * Boot-only venue: every station is `client: none`, and the boot fake gives each a PC. Absent: the answer carries none.
   */
  readonly products?: { readonly stations: boolean; readonly boot: boolean };
  /** Stations (by number) with no Clavian Station app — `client: none` — in a venue that has both products. */
  readonly noClientStations?: readonly number[];
  /** `two`: a second drawer is open at Counter 2, by Aman Verma (ADR-0029: several counters at once). */
  readonly drawers?: 'one' | 'two' | 'mine';
  /**
   * Discord (Install for players on) and WhatsApp in the library, and what the PCs said about installing them for players
   * (F-CAT-09) — in the library, the matrix and the station sheet. Off by default.
   */
  readonly playersApps?: boolean;
  /** The organisation is a franchisor with one franchisee (ADR-0019): its Venues screen lists theirs. */
  readonly franchisor?: boolean;
  /** The venue's printers and where its prints route (fake-venue-printing.ts, F-OPS-08); always answered. */
  readonly printing?: PrintingOptions;
  /**
   * The settings registry the screens are built from, in place of this file's handful: the server's real one, for the
   * settings layout's tests and screenshots (`settings-registry.ts`).
   */
  readonly registry?: readonly AnySettingDefinition[];
  /** Values stored at the organisation's scope at the start. */
  readonly organizationSettings?: Readonly<Record<string, unknown>>;
  /** Values the seeded station class keeps of its own at the start (`stationClass` scope, spec/13 §12). */
  readonly classSettings?: Readonly<Record<string, unknown>>;
  /** Venue-scope setting values stored at the start, beside the fixtures' own (e.g. `shifts.dayCloseMode: 'auto'`). */
  readonly venueSettings?: Readonly<Record<string, unknown>>;
  /** `GET …/stations` also lists a Clavian Counter PC (role `counter`, F-STN-13), for a desk printer to name. */
  readonly counterStation?: boolean;
  /**
   * Stock at the venue (fake-venue-inventory.ts, spec/13 §6.4): `off` by default, as `inventory.enabled` is; `on` a
   * café's stock with low, out and negative items; `new` just turned on, nothing counted; `counting` on, with a full
   * count open. Always answered.
   */
  readonly inventory?: InventoryOptions['mode'];
  /**
   * Guided setup's facts beyond the fake's own state (fake-setup.ts, F-HQ-03): by default the venue has a price and a
   * paired PC, so going live is not refused; a test of the Go-live gate turns them off.
   */
  readonly setup?: SetupFacts;
  /**
   * A pass catalogue at the organisation for a venue that is not `liveVenue` (which brings its own six): `GET` and `POST
   * …/passes/products`, from these. Given, guided setup's How players pay (`price`, ADR-0058) reads it — done once a
   * pass is on sale at the venue — unless `setup.price` says otherwise. `[]` is a venue with no pass yet.
   */
  readonly passes?: readonly PassProduct[];
  /** A venue with no station class yet, as a new one is: `GET …/station-classes` answers only the classes made here. */
  readonly noStationClasses?: boolean;
}

/** A pass on sale at a venue, as the server counts one for How players pay (`passesOnSaleAt`, ADR-0058). */
function passOnSaleAt(pass: PassProduct, venueId: string): boolean {
  const version = pass.version;
  const covers = version.scope.type !== 'venues' || (version.scope.venueIds ?? []).includes(venueId as never);
  return pass.active && version.channels.length > 0 && version.price.amount > 0 && covers && (version.membersOnly ?? []).length === 0;
}

export interface RecordedRequest {
  readonly method: string;
  readonly path: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly body: unknown;
}

export interface FakeResponse {
  readonly status: number;
  readonly headers: Record<string, string>;
  readonly body: string;
}

const MANAGER = [
  'venues:read',
  'venues:update',
  'settings:read',
  'settings:locker',
  'settings:sessions',
  'settings:all',
  'settings:notifications',
  'settings:kiosk',
];

export function createFakeServer(options: FakeServerOptions = {}) {
  // Offline play's fake state (spec/05 §8): made on first use, kept for the server's life.
  let offlinePlayData: { codes: { id: string; status: string; [key: string]: unknown }[]; sessions: { id: string; [key: string]: unknown }[] } | undefined;
  const offlinePlayState = () =>
    (offlinePlayData ??= {
      codes: [],
      sessions: [
        {
          id: 'ofs_01k5znw4hmfay8vvr14d2pf001',
          organizationId: 'org_01k5znw4hmfay8vvr14d2pf001',
          venueId: 'ven_01k5znw4hmfay8vvr14d2pf001',
          stationId: 'stn_01k5znw4hmfay8vvr14d2pf999',
          kind: 'staff_code',
          codeId: 'osc_01k5znw4hmfay8vvr14d2pf001',
          playerId: null,
          sessionId: null,
          startedAt: '2026-10-01T08:00:00.000Z',
          endedAt: '2026-10-01T08:45:00.000Z',
          playedSeconds: 2700,
          allowedSeconds: 3600,
          endedBy: 'reconnected',
          chargedSeconds: 0,
          charge: { amount: 0, currency: 'INR' },
          status: 'needs_attention',
          attention: 'staff_code',
          resolution: null,
          note: null,
          resolvedAt: null,
          resolvedBy: null,
          reportedAt: '2026-10-01T08:46:00.000Z',
        },
      ],
    });
  const state = {
    session: (options.signedIn ?? true) ? sessionFor(options.sessionSecondFactor === true) : (null as AuthSessionState),
    pendingTwoFactor: false,
    permissions: options.permissions ?? MANAGER,
    serverPermissions: options.serverPermissions ?? (options.permissions === 'unavailable' ? MANAGER : (options.permissions ?? MANAGER)),
    venue: venueFixture(options.venue),
    venueVersion: 1,
    settingsVersion: 1,
    values: {
      platform: { 'sessions.graceMinutes': { value: 10, locked: true } } as Record<string, StoredValue>,
      organization: Object.fromEntries(Object.entries(options.organizationSettings ?? {}).map(([key, value]) => [key, { value }])) as Record<string, StoredValue>,
      venue: {
        'venue.closingAction': { value: 'end_sessions_at_close' },
        ...Object.fromEntries(Object.entries(options.venueSettings ?? {}).map(([key, value]) => [key, { value }])),
      } as Record<string, StoredValue>,
      /** Values kept by the venue's station classes, by class id (`stationClass` scope). */
      stationClass: (options.classSettings === undefined
        ? {}
        : { [CLASS_ID]: Object.fromEntries(Object.entries(options.classSettings).map(([key, value]) => [key, { value }])) }) as Record<string, Record<string, StoredValue>>,
    },
    files: new Map<string, FileResource>(),
    requests: [] as RecordedRequest[],
    /** `auth.phoneSignIn`: a test may turn it off while a screen is open, as HQ might. */
    phoneSignIn: options.phoneSignIn ?? true,
    /** The member's other venue, read only here. At version 1 too, as a venue nobody has edited is. */
    second: venueFixture({ id: SECOND_VENUE.id, slug: SECOND_VENUE.slug, name: SECOND_VENUE.name }),
    /** The console gate starts refusing mid-session (a role joined `staff.require2faRoles`). */
    gateOn: false,
    platformRole: options.platformRole ?? null,
    organizationRole: options.organizationRole,
    /** The daily summaries an owner asked to be emailed now (`POST …/reports/daily-summary/send`). */
    dailySummarySent: [] as { day: string; venueId: string | null }[],
    /** Members' own comp limits (spec/07 §6.4), by member id: absent follows the role. */
    memberCompLimits: new Map<string, unknown>(),
    /** What the HQ guard allows, when not the role's own (`FakeServerOptions.platformServerPermissions`). */
    platformServerPermissions: options.platformServerPermissions as readonly string[] | undefined,
    /** HQ's runtime brand values and their revision (`GET/PUT /v1/hq/brand`). */
    brand: { revision: 0, updatedAt: undefined as string | undefined, values: {} as BrandRuntimeValues },
    /** Accounts made by `/sign-up/email`, keyed by lower-cased email: unverified until the OTP is spent. */
    signUps: new Map<string, { name: string; password: string; verified: boolean }>(),
    /** Station ids a device has been paired to (F-STN-02): the list then shows them a bound device. */
    pairedStations: new Set<string>(options.stationsOnline ? [STATION_ID] : []),
    /** Station ids removed through the console (ADR-0057): the list no longer shows them. */
    removedStations: new Set<string>(),
    /** Classes and zones created through the console (F-STN-21, F-STN-19), added to the seeded ones. */
    stationClasses: [] as Record<string, unknown>[],
    zones: [] as Record<string, unknown>[],
    /** Edits applied to the seeded station through `PATCH …/stations/{id}` (F-STN-01), layered on the base. */
    stationEdits: {} as Record<string, unknown>,
    /** Top-ups asked for at a PC, waiting on the desk (F-KIO-10); a test seeds them. */
    kioskTopUps: [] as { id: string; playerId: string; playerName: string | null; stationName: string; amount: { amount: number; currency: string }; bonus: { amount: number; currency: string }; status: string; createdAt: string; scanToPayReference?: string }[],
    /** The seeded station was paired before F-SES-24, so it has no offline secret: an offline code is `409`. */
    offlineSecretMissing: false,
    lockdownTested: false,
    /** How many Update now commands were sent (F-STN-12, spec/10 §8.2). */
    updateNowRequests: 0,
    /** The reason each Update now carried: `install_now` (no body) or `update_image` (a diskless PC, spec/10 §14). */
    updateNowReasons: [] as string[],
    /** Custom roles created through the console (F-IAM-11), listed after the built-in ones. */
    customRoles: [] as { name: string; permissions: string[] }[],
    /** Saved floor plans by level (F-STN-19): absent is an unarranged floor at version 0. */
    floorPlans: new Map<number, Record<string, unknown>>(),
    /** The organisation's legal name, PAN and registered address (ADR-0046). */
    businessDetails:
      options.businessDetails === 'incomplete'
        ? { name: 'Playground Gaming', legalName: null as string | null, pan: null as string | null, registeredAddress: null as Record<string, unknown> | null }
        : {
            name: 'Playground Gaming',
            legalName: 'Playground Gaming Private Limited' as string | null,
            pan: 'AAACP1234Q' as string | null,
            registeredAddress: { line1: '12 MG Road', locality: 'Bengaluru', region: 'Karnataka', postalCode: '560001', country: 'IN' } as Record<string, unknown> | null,
          },
    /** Google round trips started (`POST /v1/auth/sign-in/social`), for a test to read back. */
    socialSignIns: [] as Record<string, unknown>[],
    /** Whether `GET /v1/me` says the profile is complete, and the name it was completed with. */
    me: { profileComplete: options.profileComplete ?? true, completedWith: null as Record<string, unknown> | null },
    /** Links started (`POST /v1/auth/link-social`, `POST /v1/auth/steam/start` with `link`): path and body. */
    socialLinks: [] as { readonly path: string; readonly body: Record<string, unknown> }[],
    /** The signed-in person's ways in, as `GET /v1/auth/list-accounts` answers them. */
    linkedAccounts: (options.linkedAccounts ?? [{ providerId: 'credential' as const }]).map((account) => ({
      id: `acc-${account.providerId}`,
      providerId: account.providerId as string,
      accountId: account.providerId === 'steam' ? '76561190000000042' : `${account.providerId}-account`,
      userId: '8a6f0e1c-0000-4000-8000-000000000002',
      scopes: [] as string[],
      createdAt: account.createdAt ?? '2026-09-14T10:30:00.000Z',
      updatedAt: account.createdAt ?? '2026-09-14T10:30:00.000Z',
    })),
    /** Guided setup (F-HQ-03): the steps skipped, by venue. */
    setup: { skips: new Map() } as FakeSetupState,
    /** The pass catalogue outside `liveVenue` (`passes`), and every pass made there. */
    passes: [...(options.passes ?? [])] as PassProduct[],
  };

  function sessionFor(secondFactor: boolean): AuthSessionState {
    return {
      session: {
        id: '8a6f0e1c-0000-4000-8000-000000000001',
        token: 'session-token',
        userId: '8a6f0e1c-0000-4000-8000-000000000002',
        expiresAt: '2026-09-29T09:00:00.000Z',
        createdAt: NOW,
        updatedAt: NOW,
        secondFactor: secondFactor ? 'totp' : null,
      },
      user: {
        id: '8a6f0e1c-0000-4000-8000-000000000002',
        name: 'Riya Sharma',
        email: 'riya@playground.example',
        emailVerified: true,
        twoFactorEnabled: options.twoFactorEnabled ?? true,
        createdAt: NOW,
        updatedAt: NOW,
      },
    };
  }

  // HQ's organisations and venues (spec/14 §3.1, §3.3): the two organisations and three venues the
  // directory screens list, kept apart from the venue console's own venue above.
  const directory = createFakeHqDirectory({
    organizationId: ORGANIZATION.id,
    organizationName: ORGANIZATION.name,
    venues: [
      venueFixture(),
      venueFixture({ id: SECOND_VENUE.id, slug: SECOND_VENUE.slug, name: SECOND_VENUE.name, featured: true }),
      venueFixture({
        id: NITRO_VENUE_ID,
        organizationId: NITRO.id,
        slug: 'nitro-fc-road',
        name: 'Nitro FC Road',
        address: { line1: '4 FC Road', locality: 'Pune', region: 'Maharashtra', postalCode: '411004', country: 'IN' },
        openingHours: { mon: [], tue: [], wed: [], thu: [], fri: [{ opens: '16:00', closes: '02:00' }], sat: [{ opens: '00:00', closes: '00:00' }], sun: [] },
        status: 'setup',
        publicListing: false,
      }),
    ],
  });

  // HQ's game catalogue (spec/14 §6, F-CAT-01): the search, the mirror and the list, gated by `hq:catalog`.
  const catalogue = createFakeHqCatalogue({
    ...(options.catalogueProviderUnavailable === undefined ? {} : { providerUnavailable: options.catalogueProviderUnavailable }),
    ...(options.emptyCatalogue === undefined ? {} : { empty: options.emptyCatalogue }),
    ...(options.steamGridDbUnconfigured === undefined ? {} : { steamGridDbUnconfigured: options.steamGridDbUnconfigured }),
    ...(options.catalogueShowcase === undefined ? {} : { showcase: options.catalogueShowcase }),
  });

  // HQ's station releases (spec/14 §12, F-STN-12): releases, rollouts and the fleet, gated by `hq:releases`.
  const releases = createFakeHqReleases(options.emptyStationReleases === undefined ? {} : { empty: options.emptyStationReleases });
  // HQ's sales pipeline (F-HQ-39): leads, their timeline, templates and the import, gated by `hq:leads`. A conversion
  // makes its organisation through the directory, as `POST /v1/hq/organizations` does.
  const leads = createFakeHqLeads({
    ...(options.emptyLeads === true ? { empty: true } : {}),
    createOrganization: (body) => directory.handle({ json, problem }, 'POST', '/v1/hq/organizations', new URLSearchParams(), body as Record<string, unknown>) ?? problem(500, 'internal', 'No directory'),
  });
  // HQ's provider status page (F-HQ-38): every provider and its last check, gated by `hq:settings`.
  const providers = createFakeHqProviders();
  // HQ's online payments: the provider, the commission setting and each organisation's payee (F-PAY-05).
  const hqPayments = createFakeHqPayments((organizationId) => {
    const stored = organizationId === ORGANIZATION.id ? state.values.organization['payments.commissionBps']?.value : undefined;
    return typeof stored === 'number' ? stored : 0;
  });
  // HQ's add-ons (F-BOOT-17): the catalogue and each organisation's, in rupees like every fake organisation.
  const hqAddons = createFakeHqAddons(() => 'INR');
  // Moves from another system (F-HQ-40): HQ's runs on this organisation, and the venue's "Moved" list.
  const migrations = createFakeHqMigrations({
    organizationId: ORGANIZATION.id,
    venues: () => directory.state.venues.filter((venue) => venue.organizationId === ORGANIZATION.id).map((venue) => ({ id: venue.id, name: venue.name })),
    ...(options.movedPlayers === true ? { moved: movedPlayerFixtures(VENUE_ID) } : {}),
    ...(options.migrationVenueTaxMode === undefined ? {} : { venueTaxMode: options.migrationVenueTaxMode }),
  });

  /**
   * The permission an HQ route is gated by (the contract's `x-clavian-permission`): the organisations
   * and venues routes' `hq:organizations`, the catalogue's `hq:catalog`, and `hq:settings` for the
   * settings — one organisation's included — and the brand.
   */
  const hqPermissionFor = (pathname: string): string =>
    pathname.startsWith('/v1/hq/catalogue')
      ? 'hq:catalog'
      : /^\/v1\/hq\/(station-(releases|rollouts|fleet)|boot-server-release(s|-channels))(\/|$)/.test(pathname)
        ? 'hq:releases'
        : /^\/v1\/hq\/(leads|lead-templates)(\/|$)/.test(pathname)
          ? 'hq:leads'
      : pathname === '/v1/hq/venues' ||
          pathname === '/v1/hq/live' ||
          pathname === '/v1/hq/setup' ||
          /^\/v1\/hq\/organizations(\/[^/]+(\/owner|\/owner-invitations(\/[^/]+(\/resend|\/link)?)?|\/venues\/[^/]+\/listing|\/migrations(\/[^/]+(\/[a-z-]+)?)?)?)?$/.test(pathname)
        ? 'hq:organizations'
        : 'hq:settings';

  const venueEtag = () => `"venue-${state.venueVersion}"`;
  const settingsEtag = () => `"settings-${state.settingsVersion}"`;

  // -- settings views ------------------------------------------------------------------------

  // The registry the screens are built from: the test's own, or this file's handful.
  const registry: Readonly<Record<string, AnySettingDefinition>> =
    options.registry === undefined ? definitions : Object.fromEntries(options.registry.map((definition) => [definition.key, definition]));

  function entry(definition: AnySettingDefinition, screen: 'venue' | 'organization'): SettingEntry {
    const described = describeSetting(definition);
    const platform = state.values.platform[definition.key];
    const organization = state.values.organization[definition.key];
    const venue = screen === 'venue' ? state.values.venue[definition.key] : undefined;
    const chain: [SettingEntry['source'], StoredValue | undefined][] = [
      ['venue', venue],
      ['organization', organization],
      ['platform', platform],
    ];
    const inForce = platform?.locked ? (['platform', platform] as const) : chain.find(([, stored]) => stored !== undefined);
    const own = screen === 'venue' ? venue : organization;
    const above = chain.filter(([source]) => source !== screen).find(([, stored]) => stored !== undefined);
    const canEdit = state.serverPermissions.includes(definition.editPermission) && !platform?.locked && definition.dated !== true;
    // A setting set above the venue only is not the venue's to change (`editableScopes` has no venue).
    const settableHere = definition.scopes.includes(screen);
    const classScoped = screen === 'venue' && definition.scopes.includes('stationClass');
    const overrides = classScoped
      ? Object.entries(state.values.stationClass)
          .filter(([, values]) => values[definition.key] !== undefined)
          .map(([classId, values]) => ({ scopeType: 'stationClass' as const, stationClassId: classId, zoneId: null, value: values[definition.key]?.value, updatedAt: NOW }))
      : [];
    // The organisation's screen names the venues that keep their own value — for a member who covers every venue.
    const venueOverrides =
      screen === 'organization' && definition.scopes.includes('venue') && !options.venueOnly
        ? state.values.venue[definition.key] === undefined
          ? []
          : [{ venueId: VENUE_ID, value: state.values.venue[definition.key]?.value, updatedAt: NOW }]
        : undefined;
    const editableHere = canEdit && settableHere;
    return {
      key: definition.key,
      group: definition.group,
      dated: definition.dated === true,
      description: definition.description,
      schema: described.schema,
      default: definition.default,
      scopes: [...definition.scopes],
      visibleTo: definition.visibleTo,
      editPermission: definition.editPermission,
      ...(described.unit === undefined ? {} : { unit: described.unit }),
      value: inForce === undefined ? definition.default : inForce[1]?.value,
      source: inForce === undefined ? 'default' : inForce[0],
      inherited: above === undefined ? { value: definition.default, source: 'default' } : { value: above[1]?.value, source: above[0] },
      hasOverride: own !== undefined,
      ...(platform?.locked ? { lockedAt: 'platform' as const } : {}),
      lockableAt: [...definition.lockableAt],
      editable: editableHere,
      editableScopes: editableHere ? [screen, ...(classScoped ? ['stationClass' as const] : [])] : [],
      overrides,
      ...(venueOverrides === undefined ? {} : { venueOverrides }),
    };
  }

  function view(screen: 'venue' | 'organization'): SettingsView {
    // "On a screen when it is visible to that screen and settable at that scope or above" (spec/13 §13).
    const visible: readonly string[] = screen === 'venue' ? ['venue'] : ['organization', 'venue'];
    const settable: readonly string[] = screen === 'venue' ? ['venue', 'organization', 'platform'] : ['organization', 'platform'];
    const settings = Object.values(registry)
      .filter((definition) => visible.includes(definition.visibleTo) && definition.scopes.some((scope) => settable.includes(scope) || (screen === 'venue' && scope === 'stationClass')))
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((definition) => entry(definition, screen));
    const groups = [...new Set(settings.map((setting) => setting.group))];
    const order = Object.keys(GROUP_TITLES);
    groups.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return {
      scope: { type: screen, organizationId: ORGANIZATION.id, venueId: screen === 'venue' ? VENUE_ID : null, currency: 'INR' },
      groups: groups.map((id) => ({ id, title: GROUP_TITLES[id] ?? id })),
      settings,
    } as SettingsView;
  }

  // -- HQ's settings views (spec/14 §14) -----------------------------------------------------
  //
  // HQ sees every setting on the platform's screen, and on one organisation's every setting that is
  // set there or above. It may change a value at the screen's own scope where the setting accepts it,
  // and lock one where it is lockable; a lock at the screen's own scope is its own and binds only the
  // scopes below, a platform lock binds the organisation's screen too (the server's `settings-walk.ts`).

  const platformLocked = (definition: AnySettingDefinition) =>
    state.values.platform[definition.key]?.locked === true && definition.lockableAt.includes('platform');

  function hqEntry(definition: AnySettingDefinition, screen: 'platform' | 'organization'): SettingEntry {
    const described = describeSetting(definition);
    const platform = definition.scopes.includes('platform') ? state.values.platform[definition.key] : undefined;
    const organization = screen === 'organization' && definition.scopes.includes('organization') ? state.values.organization[definition.key] : undefined;
    const organizationLocked = organization?.locked === true && definition.lockableAt.includes('organization');
    let value: unknown = definition.default;
    let source: SettingEntry['source'] = 'default';
    let lockedAt: 'platform' | 'organization' | undefined;
    if (platformLocked(definition)) [value, source, lockedAt] = [platform?.value, 'platform', 'platform'];
    else if (organization !== undefined) [value, source, lockedAt] = [organization.value, 'organization', organizationLocked ? 'organization' : undefined];
    else if (platform !== undefined) [value, source] = [platform.value, 'platform'];
    const inherited =
      screen === 'organization' && platform !== undefined
        ? { value: platform.value, source: 'platform' as const }
        : { value: definition.default, source: 'default' as const };
    const editable = screen === 'platform' ? definition.scopes.includes('platform') : definition.scopes.includes('organization') && !platformLocked(definition);
    return {
      key: definition.key,
      group: definition.group,
      dated: definition.dated === true,
      description: definition.description,
      schema: described.schema,
      default: definition.default,
      scopes: [...definition.scopes],
      visibleTo: definition.visibleTo,
      editPermission: definition.editPermission,
      ...(described.unit === undefined ? {} : { unit: described.unit }),
      value,
      source,
      inherited,
      hasOverride: (screen === 'platform' ? platform : organization) !== undefined,
      ...(lockedAt === undefined ? {} : { lockedAt }),
      lockableAt: [...definition.lockableAt],
      editable,
      editableScopes: editable ? [screen] : [],
      overrides: [],
    };
  }

  function hqView(screen: 'platform' | 'organization'): SettingsView {
    const settings = Object.values(registry)
      .filter((definition) => screen === 'platform' || definition.scopes.some((scope) => scope === 'organization' || scope === 'platform'))
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((definition) => hqEntry(definition, screen));
    const order = Object.keys(GROUP_TITLES);
    const groups = [...new Set(settings.map((setting) => setting.group))].sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return {
      scope: { type: screen, organizationId: screen === 'organization' ? ORGANIZATION.id : null, venueId: null, currency: 'INR' },
      groups: groups.map((id) => ({ id, title: GROUP_TITLES[id] ?? id })),
      settings,
    } as SettingsView;
  }

  // -- the brand (spec/14 §14, spec/03 §12.1) -------------------------------------------------

  const brandEtag = () => `"brand-r${state.brand.revision}"`;

  function brandView(): HqBrandView {
    return {
      revision: state.brand.revision,
      ...(state.brand.updatedAt === undefined ? {} : { updatedAt: state.brand.updatedAt }),
      values: state.brand.values,
      deployment: DEPLOYMENT_BRAND,
      messaging: { email: { name: 'Clavian', address: 'no-reply@clavian.io' } },
      // What clients get; the brand screen does not read it.
      brand: {} as HqBrandView['brand'],
    };
  }

  // -- responses -----------------------------------------------------------------------------

  const json = (status: number, body: unknown, headers: Record<string, string> = {}): FakeResponse => ({
    status,
    headers: { 'content-type': 'application/json', 'x-request-id': REQUEST_ID, ...headers },
    body: JSON.stringify(body),
  });

  const problem = (status: number, code: string, title: string, extra: Partial<Problem> = {}, headers: Record<string, string> = {}): FakeResponse => ({
    status,
    headers: { 'content-type': 'application/problem+json', 'x-request-id': REQUEST_ID, ...headers },
    body: JSON.stringify({
      type: `https://errors.clavian.io/${code.replaceAll('_', '-')}`,
      title,
      status,
      code,
      requestId: REQUEST_ID,
      ...extra,
    } satisfies Problem),
  });

  const authError = (status: number, code: string, message: string): FakeResponse => json(status, { code, message });

  /** HQ's Players and the platform audit log (fake-hq-players-audit.ts), each route with its own permission. */
  const playersAndAudit = createHqPlayersAudit({ json, problem, requestId: REQUEST_ID });
  // The specialist answers first where two fakes serve a path: commerce owns the kitchen tickets and
  // the shifts fake owns the day close; the floor fake reads both only for its summary figures.
  /** A player's on account (fake-venue-credit.ts), each route with its own permission; answered with `liveVenue`. */
  const credit = createFakeCredit({ json, problem, gate, can: (permission) => state.serverPermissions.includes(permission) });
  /** The venue's tax mode and GSTIN (fake-venue-tax.ts), each route with its own permission; always answered. */
  const tax = createFakeTax({
    json,
    problem,
    gate,
    can: (permission) => state.serverPermissions.includes(permission),
    businessDetailsComplete: () => state.businessDetails.legalName !== null && state.businessDetails.registeredAddress !== null,
  });
  const rateCard = createFakeRateCard({
    json,
    problem,
    can: (permission) => state.serverPermissions.includes(permission),
    flatRate: () => {
      const stored = state.values.venue['sessions.paygHourlyRate']?.value ?? state.values.organization['sessions.paygHourlyRate']?.value;
      return typeof stored === 'number' ? stored : 6000;
    },
  });
  /** The organisation's players and wallets (fake-venue-players.ts); a sale's wallet tender draws them. */
  const players = createFakePlayers({
    json,
    problem,
    gate,
    setting: (key) => state.values.venue[key]?.value ?? state.values.organization[key]?.value,
    // The till's fake (below) knows whether the signed-in member holds an open drawer: cash needs one (ADR-0031).
    cashNeedsShift: () => ops.cashNeedsShift(),
  });
  /**
   * The organisation's promotions, discount codes and coupon batches (fake-org-promotions.ts), each
   * route with its own permission; always answered. A redeemed coupon adds to a player's wallet bonus.
   */
  const promotions = createFakePromotions({ json, problem, gate, can: (permission) => state.serverPermissions.includes(permission), grantBonus: players.grantBonus });
  /** The venue's sales (fake-venue-commerce.ts); the payments fake records a captured online payment on them. */
  /** The till, the pass catalogue and the library (fake-venue-ops.ts); its passes price the Sell screen's pass lines. */
  const ops = createFakeOps({
    json,
    problem,
    gate,
    setting: (key) => state.values.venue[key]?.value ?? state.values.organization[key]?.value,
    can: (permission) => state.serverPermissions.includes(permission),
    ...(options.drawers === undefined ? {} : { drawers: options.drawers }),
    ...(options.playersApps === true ? { playersApps: true } : {}),
  });
  const boot =
    options.boot === undefined || options.boot === false
      ? undefined
      : createFakeBoot(
          {
            json,
            problem,
            gate,
            can: (permission) => state.serverPermissions.includes(permission),
            // Make it station (ADR-0065): the floor fake draws the new station; absent without `liveVenue`.
            makeStation: (spec) => floorFake?.addStation(spec),
            classOf: (stationId) => floorFake?.classOf(stationId),
            bootOnly: options.products?.stations === false,
          },
          options.boot === true ? 'ready' : (options.boot as BootFakeMode),
        );
  /** The venue's guest passes (fake-venue-guests.ts, F-SES-07): minted and activated by the sales, always answered. */
  const guests = createFakeGuests(
    { json, problem, gate, can: (permission) => state.serverPermissions.includes(permission), enabled: () => options.guests === true || options.guests === 'seeded' },
    { seeded: options.guests === 'seeded' },
  );
  const commerce = createFakeCommerce({ json, problem, gate, can: (permission) => state.serverPermissions.includes(permission), wallet: players, credit, codes: promotions, promotions, passes: ops, guests });
  /** Printers, print jobs, printing and the cash drawer (fake-venue-printing.ts, F-OPS-08/-09); always answered. */
  const printing = createFakePrinting(
    { json, problem, can: (permission) => state.serverPermissions.includes(permission), tickets: () => commerce.state.tickets },
    options.printing,
  );
  /** Online payments (fake-venue-payments.ts): the Online (UPI) tender and the wallet's online top-up, with `liveVenue`. */
  const payments = createFakePayments({
    json,
    problem,
    gate,
    can: (permission) => state.serverPermissions.includes(permission),
    sales: commerce,
    wallets: players,
  });
  /** The venue's stock (fake-venue-inventory.ts); always answered, off unless `inventory` says otherwise. */
  const inventory = createFakeInventory(
    { json, problem, can: (permission) => state.serverPermissions.includes(permission) },
    options.inventory === undefined ? {} : { mode: options.inventory },
  );
  const floorFake =
    options.liveVenue === true
      ? createFakeFloor({
          json,
          problem,
          gate,
          can: (permission) => state.serverPermissions.includes(permission),
          onConsoleOrder: (order) => commerce.addConsoleSessionOrder(order),
          clientOf: (number) => (options.products?.stations === false || options.noClientStations?.includes(number) === true ? 'none' : 'station'),
          madeClient: () => (options.products?.stations === false ? 'none' : 'station'),
          pcsOnly: options.products?.stations === false,
        })
      : undefined;
  const liveVenue = floorFake === undefined ? [] : [credit, payments, commerce, ops, players, floorFake];

  /** The signed-in member's own account and inbox (fake-account.ts). */
  const account = createFakeAccount({
    json,
    problem,
    organizationId: ORGANIZATION.id,
    // The daily summary is an owner's to subscribe to (ADR-0044): the role `GET /v1/me/access` answers.
    isOwner: () => state.organizationRole === 'owner',
    // What `GET /v1/me/venues` lists below: the one venue, both with `venues: 'two'`, none with `noVenues`.
    venues: () => (options.noVenues ? [] : [state.venue, ...(options.venues === 'two' ? [state.second] : [])]),
  });
  /** The organisation's Venues screen and its franchisees (fake-org-venues.ts). */
  const orgVenues = createFakeOrgVenues({
    json,
    problem,
    can: (permission) => state.serverPermissions.includes(permission),
    organizationId: ORGANIZATION.id,
    seeded: () => (options.venues === 'two' ? [state.venue, state.second] : [state.venue]),
    etagOf: (venueId) => (venueId === VENUE_ID ? venueEtag() : venueId === SECOND_VENUE.id ? '"venue-1"' : undefined),
    ...(options.franchisor === true ? { franchisor: true } : {}),
  });

  function gate(): FakeResponse | undefined {
    if (state.session === null) return problem(401, 'unauthenticated', 'Not signed in');
    if ((options.secondFactorRequired || state.gateOn) && state.session.session.secondFactor == null) {
      return problem(403, 'second_factor_required', 'Second factor required');
    }
    return undefined;
  }

  // -- HQ's routes, past the guard -----------------------------------------------------------

  function hq(method: string, pathname: string, headers: Readonly<Record<string, string>>, input: Record<string, unknown>): FakeResponse {
    const ifMatch = headers['if-match'];

    if (pathname === '/v1/hq/brand') {
      if (method === 'GET') return json(200, brandView(), { etag: brandEtag() });
      if (method !== 'PUT') return problem(405, 'method_not_allowed', 'Method not allowed');
      if (ifMatch === undefined) return problem(400, 'validation_failed', 'If-Match required');
      if (ifMatch !== brandEtag()) return problem(412, 'stale_write', 'Stale write', {}, { etag: brandEtag() });
      // The contract's own schema, so a colour below AA is refused in the server's words.
      const parsed = BrandRuntimeChangeSchema.safeParse(input);
      if (!parsed.success) {
        return problem(400, 'validation_failed', 'Invalid', {
          errors: parsed.error.issues.map((issue) => ({ field: issue.path.join('.'), code: issue.code, message: issue.message })),
        });
      }
      const next: Record<string, unknown> = { ...state.brand.values };
      for (const group of BRAND_RUNTIME_GROUPS) {
        if (parsed.data[group] !== undefined) next[group] = parsed.data[group];
      }
      for (const group of parsed.data.reset ?? []) delete next[group];
      // A save that changes nothing is no new revision (spec/03 §12.1).
      if (JSON.stringify(next) !== JSON.stringify(state.brand.values)) {
        state.brand = { revision: state.brand.revision + 1, updatedAt: NOW, values: next as BrandRuntimeValues };
      }
      return json(200, brandView(), { etag: brandEtag() });
    }

    const screen = pathname === '/v1/hq/settings' ? 'platform' : pathname === HQ_ORGANIZATION_SETTINGS ? 'organization' : undefined;
    if (screen === undefined) {
      if (/^\/v1\/hq\/organizations\/[^/]+\/settings$/.test(pathname)) return problem(404, 'not_found', 'No such organisation.');
      return problem(404, 'not_found', `No fake route for ${method} ${pathname}`);
    }
    if (method === 'GET') return json(200, hqView(screen), { etag: settingsEtag() });
    if (method !== 'PUT') return problem(405, 'method_not_allowed', 'Method not allowed');
    if (ifMatch === undefined) return problem(400, 'validation_failed', 'If-Match required');
    if (ifMatch !== settingsEtag()) return problem(412, 'stale_write', 'Stale write', {}, { etag: settingsEtag() });
    const changes = (input['changes'] ?? []) as { key: string; value?: unknown; reset?: boolean; locked?: boolean }[];
    for (const [index, change] of changes.entries()) {
      const definition = registry[change.key];
      if (definition === undefined || !definition.scopes.includes(screen)) return problem(400, 'validation_failed', 'Not settable here');
      if (change.locked === true && !definition.lockableAt.includes(screen)) {
        return problem(400, 'validation_failed', 'Invalid', {
          errors: [{ field: `changes.${index}.locked`, code: 'custom', message: `"${change.key}" is never locked.` }],
        });
      }
      if (screen === 'organization' && platformLocked(definition)) return problem(403, 'setting_locked', 'Setting locked');
    }
    for (const change of changes) {
      const stored = state.values[screen];
      if (change.reset) delete stored[change.key];
      else stored[change.key] = { value: change.value, locked: change.locked ?? stored[change.key]?.locked ?? false };
    }
    state.settingsVersion += 1;
    return json(200, hqView(screen), { etag: settingsEtag() });
  }

  // -- the routes ----------------------------------------------------------------------------

  function handle(method: string, url: string, headers: Readonly<Record<string, string>>, body: unknown): FakeResponse {
    const { pathname, searchParams } = new URL(url, 'http://console.test');
    state.requests.push({ method, path: `${pathname}${searchParams.size > 0 ? `?${searchParams}` : ''}`, headers, body });
    const input = (body ?? {}) as Record<string, unknown>;

    // better-auth
    if (pathname === '/v1/auth/get-session' && method === 'GET') return json(200, state.session);
    if (pathname === '/v1/auth/sign-out' && method === 'POST') {
      state.session = null;
      return json(200, { success: true });
    }
    if ((pathname === '/v1/auth/sign-in/email' || pathname === '/v1/auth/sign-in/username') && method === 'POST') {
      // A sign-up's own account (spec/07 §3.1: unverified cannot sign in), ahead of the fixed one.
      const signUpEmail = pathname.endsWith('email') && typeof input['email'] === 'string' ? input['email'].trim().toLowerCase() : undefined;
      const signedUp = signUpEmail === undefined ? undefined : state.signUps.get(signUpEmail);
      if (signedUp !== undefined) {
        if (!signedUp.verified) return authError(403, 'EMAIL_NOT_VERIFIED', 'Email not verified');
        if (input['password'] !== signedUp.password) return authError(401, 'INVALID_EMAIL_OR_PASSWORD', 'Invalid');
        state.session = sessionFor(false);
        return json(200, { token: 'session-token', user: state.session?.user });
      }
      if (input['password'] !== PASSWORD) {
        return authError(401, pathname.endsWith('email') ? 'INVALID_EMAIL_OR_PASSWORD' : 'INVALID_USERNAME_OR_PASSWORD', 'Invalid');
      }
      if (options.twoFactorOnPassword) {
        state.pendingTwoFactor = true;
        return json(200, { twoFactorRedirect: true, twoFactorMethods: ['totp'] });
      }
      state.session = sessionFor(false);
      return json(200, { token: 'session-token', user: state.session?.user });
    }
    // Phone sign-in off: every phone endpoint is refused, as the server's before-hook refuses it.
    if (pathname.startsWith('/v1/auth/phone-number/') && !state.phoneSignIn) return authError(403, 'PHONE_SIGN_IN_OFF', 'Signing in with a phone number is not available right now.');
    if (pathname === '/v1/auth/phone-number/send-otp' && method === 'POST') return json(200, { message: 'code sent' });
    if (pathname === '/v1/auth/phone-number/verify' && method === 'POST') {
      if (input['code'] !== PHONE_CODE) return authError(400, 'INVALID_OTP', 'Invalid code');
      // A number with no account: refused without the terms, and the code kept for the retry (spec/07 §3.1).
      if (input['phoneNumber'] === NEW_OWNER_PHONE && input['consent'] === undefined) {
        return authError(400, 'CONSENT_REQUIRED', 'Accept the terms and the privacy policy to create an account.');
      }
      state.session = sessionFor(false);
      return json(200, { status: true, token: 'session-token', user: state.session?.user });
    }
    // Sign-up by email and password, then email OTP verification (F-IAM-02, F-CMP-06, spec/07 §2.1,
    // §3.1): the account is created unverified, and verifying never signs a person in on its own
    // (`token` is always `null`) — only `sign-in/email`, above, does, once `verified` is true.
    if (pathname === '/v1/auth/sign-up/email' && method === 'POST') {
      const email = typeof input['email'] === 'string' ? input['email'].trim().toLowerCase() : '';
      const name = typeof input['name'] === 'string' ? input['name'] : '';
      const password = typeof input['password'] === 'string' ? input['password'] : '';
      const consent = input['consent'] as { terms?: unknown; privacy?: unknown } | undefined;
      if (consent === undefined || typeof consent.terms !== 'string' || typeof consent.privacy !== 'string') {
        return authError(400, 'CONSENT_REQUIRED', 'Accept the terms and the privacy policy to create an account.');
      }
      if (password === BREACHED_PASSWORD) return authError(400, 'PASSWORD_COMPROMISED', 'Password compromised');
      if (password.length < 8) return authError(400, 'PASSWORD_TOO_SHORT', 'Password too short');
      if (email === 'riya@playground.example' || state.signUps.has(email)) {
        return authError(422, 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL', 'User already exists. Use another email.');
      }
      // A competing vendor's domain (spec/07 §3.1): the server refuses the account; one stands in for the list here.
      if (email.endsWith('@ggleap.com')) {
        return authError(403, 'SIGN_UP_REFUSED', 'An account cannot be created with this email address.');
      }
      state.signUps.set(email, { name, password, verified: false });
      return json(200, { token: null, user: { id: `usr_${email}`, name, email, emailVerified: false, createdAt: NOW, updatedAt: NOW } });
    }
    if (pathname === '/v1/auth/email-otp/send-verification-otp' && method === 'POST') return json(200, { success: true });
    if (pathname === '/v1/auth/email-otp/verify-email' && method === 'POST') {
      const email = typeof input['email'] === 'string' ? input['email'].trim().toLowerCase() : '';
      if (input['otp'] !== EMAIL_OTP_CODE) return authError(400, 'INVALID_OTP', 'Invalid code');
      const signedUp = state.signUps.get(email);
      if (signedUp !== undefined) signedUp.verified = true;
      return json(200, { status: true, token: null, user: { id: `usr_${email}`, name: signedUp?.name ?? '', email, emailVerified: true, createdAt: NOW, updatedAt: NOW } });
    }
    if ((pathname === '/v1/auth/two-factor/verify-totp' || pathname === '/v1/auth/two-factor/verify-backup-code') && method === 'POST') {
      if (input['code'] !== TOTP_CODE) return authError(401, 'INVALID_CODE', 'Invalid code');
      state.pendingTwoFactor = false;
      state.session = sessionFor(true);
      return json(200, { token: 'session-token-2', user: { id: state.session?.user.id, twoFactorEnabled: true } });
    }
    // Replacing the backup codes and turning the factor off (F-IAM-09): the session must have proved
    // the second factor, and a password, when sent, must be the account's.
    if ((pathname === '/v1/auth/two-factor/generate-backup-codes' || pathname === '/v1/auth/two-factor/disable') && method === 'POST') {
      if (state.session === null) return authError(401, 'UNAUTHORIZED', 'Not signed in');
      if (state.session.session.secondFactor == null) return authError(403, 'SECOND_FACTOR_REQUIRED', 'Second factor required');
      if (input['password'] !== undefined && input['password'] !== PASSWORD) return authError(400, 'INVALID_PASSWORD', 'Invalid password');
      if (pathname.endsWith('disable')) {
        state.session = { ...state.session, user: { ...state.session.user, twoFactorEnabled: false } };
        return json(200, { status: true });
      }
      return json(200, { status: true, backupCodes: ['k3m9-q2x7', 'p8r4-w6t1', 'z5n2-h7c3'] });
    }
    // The providers' browser flow (F-IAM-03 … F-IAM-05): the api answers the provider's URL; the round trip itself is
    // the browser's. Steam starts at its own endpoint.
    if ((pathname === '/v1/auth/sign-in/social' || (pathname === '/v1/auth/steam/start' && input['link'] !== true)) && method === 'POST') {
      const provider = pathname === '/v1/auth/steam/start' ? 'steam' : String(input['provider']);
      state.socialSignIns.push(pathname === '/v1/auth/steam/start' ? { ...input, provider } : input);
      if (!(options.socialProviders ?? []).includes(provider as 'google')) return authError(404, 'PROVIDER_NOT_FOUND', 'Provider not found');
      if (provider === 'google') return json(200, { redirect: true, url: 'https://accounts.google.test/o/oauth2/v2/auth?state=fake-state' });
      const url = `https://${provider}.provider.test/authorize?state=fake-state`;
      return json(200, provider === 'steam' ? { url } : { redirect: true, url });
    }
    if (pathname === '/v1/me' && method === 'GET') {
      if (state.session === null) return problem(401, 'unauthenticated', 'Not signed in');
      return json(200, {
        id: 'ply_01',
        username: 'riya',
        displayName: null,
        marketingOptIn: false,
        email: state.session.user.email,
        emailVerified: true,
        phoneNumber: null,
        phoneNumberVerified: false,
        profileComplete: state.me.profileComplete,
      });
    }
    if (pathname === '/v1/me/profile/complete' && method === 'POST') {
      if (state.session === null) return problem(401, 'unauthenticated', 'Not signed in');
      state.me = { profileComplete: true, completedWith: input };
      const name = typeof input['name'] === 'string' ? input['name'] : state.session.user.name;
      state.session = { ...state.session, user: { ...state.session.user, name } };
      return json(200, { id: 'ply_01', username: 'riya', displayName: input['displayName'], marketingOptIn: false, email: state.session.user.email, emailVerified: true, phoneNumber: null, phoneNumberVerified: false, profileComplete: true });
    }
    // Linking a provider from the session, listing the ways in, unlinking one (spec/07 §2 "Account linking").
    if ((pathname === '/v1/auth/link-social' || (pathname === '/v1/auth/steam/start' && input['link'] === true)) && method === 'POST') {
      const provider = pathname === '/v1/auth/steam/start' ? 'steam' : String(input['provider']);
      state.socialLinks.push({ path: pathname, body: input });
      if (!(options.socialProviders ?? []).includes(provider as 'google')) return authError(404, 'PROVIDER_NOT_FOUND', 'Provider not found');
      if (state.session === null) return authError(401, 'UNAUTHORIZED', 'Not signed in');
      const url = `https://${provider}.provider.test/authorize?state=fake-state`;
      return json(200, pathname === '/v1/auth/steam/start' ? { url } : { redirect: false, url });
    }
    if (pathname === '/v1/auth/list-accounts' && method === 'GET') {
      if (state.session === null) return authError(401, 'UNAUTHORIZED', 'Not signed in');
      return json(200, state.linkedAccounts);
    }
    if (pathname === '/v1/auth/unlink-account' && method === 'POST') {
      if (state.session === null) return authError(401, 'UNAUTHORIZED', 'Not signed in');
      if (options.staleSession === true) return authError(403, 'SESSION_NOT_FRESH', 'Session is not fresh');
      const target = state.linkedAccounts.find((account) => account.id === input['accountId']);
      if (state.linkedAccounts.length <= 1) return authError(400, 'FAILED_TO_UNLINK_LAST_ACCOUNT', "You can't unlink your last account");
      if (target === undefined) return authError(400, 'ACCOUNT_NOT_FOUND', 'Account not found');
      if (target.providerId === 'credential') return authError(400, 'PASSWORD_NOT_UNLINKABLE', 'A password is not a linked account');
      state.linkedAccounts = state.linkedAccounts.filter((account) => account !== target);
      return json(200, { status: true });
    }
    if (pathname === '/v1/auth/two-factor/enable' && method === 'POST') {
      return json(200, {
        method: 'totp',
        totpURI: 'otpauth://totp/Clavian:riya%40playground.example?secret=JBSWY3DPEHPK3PXP&issuer=Clavian',
        backupCodes: ['aaaa-bbbb', 'cccc-dddd'],
      });
    }

    // me (spec/08 §4.2, spec/07 §6.3): `/me/access` in the contract's `MyAccess` shape — at a venue,
    // in an organisation, or with neither at platform scope.
    if (pathname === '/v1/me/access' && method === 'GET') {
      if (state.session === null) return problem(401, 'unauthenticated', 'Not signed in');
      // With neither an organisation nor a venue: the platform role's bundle (spec/07 §7).
      if (!searchParams.has('venueId') && !searchParams.has('organizationId')) {
        const access: MyAccessResponse = {
          scope: 'platform',
          organizationId: null,
          venueId: null,
          memberId: null,
          role: state.platformRole,
          permissions: [...(options.platformAccessPermissions ?? permissionsForPlatformRole(state.platformRole))].sort(),
          limits: null,
          secondFactor: { required: state.platformRole !== null, satisfied: state.session.session.secondFactor != null },
        };
        return json(200, access);
      }
      if (state.permissions === 'unavailable') return problem(404, 'not_found', 'Not found');
      const venueId = searchParams.get('venueId');
      const access: MyAccessResponse = {
        scope: venueId === null ? 'organization' : 'venue',
        organizationId: ORGANIZATION.id,
        venueId,
        memberId: 'mem_01k5znw4hmfay8vvr14d2pf2db',
        role: state.organizationRole ?? (state.permissions.includes('venues:update') ? 'manager' : 'cashier'),
        permissions: [...state.permissions].sort(),
        limits: null,
        secondFactor: {
          required: options.secondFactorRequired === true,
          satisfied: state.session.session.secondFactor != null,
        },
        ...(options.products === undefined ? {} : { products: options.products }),
      };
      return json(200, access);
    }
    // In #466's shapes: `MyOrganizationList` and `MyVenueList`, `{ data }` with no page.
    if (pathname === '/v1/me/organizations' && method === 'GET') {
      if (state.session === null) return problem(401, 'unauthenticated', 'Not signed in');
      const organization: MyOrganizationResponse = {
        ...ORGANIZATION,
        name: options.organizationName ?? ORGANIZATION.name,
        currency: 'INR',
        memberId: 'mem_01k5znw4hmfay8vvr14d2pf2db',
        role: options.organizationRole ?? (state.permissions !== 'unavailable' && state.permissions.includes('venues:update') ? 'manager' : 'cashier'),
        allVenues: options.venueOnly !== true,
        secondFactor: { required: options.secondFactorRequired === true, satisfied: state.session.session.secondFactor != null },
      };
      const other: MyOrganizationResponse[] =
        options.otherOrganization === undefined
          ? []
          : [
              {
                ...organization,
                ...OTHER_ORGANIZATION,
                memberId: 'mem_01k5znw4hmfay8vvr14d2pf3aa',
                role: options.otherOrganization === 'every-venue' ? 'owner' : 'manager',
                allVenues: options.otherOrganization === 'every-venue',
              },
            ];
      // By name, as the server orders them.
      return json(200, { data: [...other, organization] });
    }
    if (pathname === '/v1/me/venues' && method === 'GET') {
      if (state.session === null) return problem(401, 'unauthenticated', 'Not signed in');
      const only = searchParams.get('organizationId');
      const otherIds: string[] = options.otherOrganization === undefined ? [] : [OTHER_ORGANIZATION.id];
      if (only !== null && only !== ORGANIZATION.id && !otherIds.includes(only)) return problem(404, 'not_found', 'Not found');
      const venue = (from: Venue): MyVenueResponse => ({
        id: from.id,
        organizationId: from.organizationId,
        name: from.name,
        slug: from.slug,
        timezone: from.timezone,
        currency: from.currency,
        status: from.status,
      });
      // Venues created through the console (fake-org-venues.ts) join the member's own, so a new venue's Setup opens.
      const made = orgVenues.state.created.filter((one) => !orgVenues.state.archived.has(one.id)).map(venue);
      const own = options.noVenues ? made : [venue(state.venue), ...(options.venues === 'two' ? [venue(state.second)] : []), ...made];
      const others: MyVenueResponse[] =
        options.otherOrganization === undefined
          ? []
          : OTHER_VENUES.map((one) => ({ ...venue(state.venue), id: one.id, slug: one.slug, name: one.name, organizationId: OTHER_ORGANIZATION.id }));
      const venues = [...others, ...own].filter((one) => only === null || one.organizationId === only);
      return json(200, { data: venues });
    }

    // The brand clients read before anyone signs in (spec/03 §12): only what the consoles use of it.
    if (pathname === '/v1/meta' && method === 'GET') {
      return json(200, {
        brand: {
          domains: { api: 'api.clavian.test', app: 'app.clavian.test', play: 'play.clavian.test', linkHosts: [] },
          ...(options.legal === 'unavailable' ? {} : { legal: LEGAL }),
        },
        flags: {},
        signIn: { social: [...(options.socialProviders ?? [])], phone: state.phoneSignIn },
      });
    }

    // Staff and roles (spec/07 §6, F-IAM-08/-11, F-ORG-13): the members, the invitations out and every
    // role with its permissions, read only — enough for the Staff screen and its sheets to be scanned.
    // A role's limits (spec/07 §6.4): checked with the contract's schema and echoed back on the role.
    const limitsWrite = /^\/v1\/organizations\/[^/]+\/roles\/([a-z_]+)\/limits$/.exec(pathname);
    if (limitsWrite !== null && method === 'PUT') {
      const refused = gate();
      if (refused !== undefined) return refused;
      const name = limitsWrite[1] as (typeof ORGANIZATION_ROLES)[number];
      if (!ORGANIZATION_ROLES.includes(name)) return problem(404, 'not_found', 'Not found');
      const parsed = RoleLimitsSchema.safeParse(input);
      if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed');
      return json(200, { name, builtIn: true, permissions: [...ORGANIZATION_ROLE_GRANTS[name]], limits: parsed.data });
    }
    // A member's own comp limit (spec/07 §6.4): checked with the contract's schema, kept, and read back.
    const memberLimits = /^\/v1\/organizations\/[^/]+\/members\/([^/]+)\/limits$/.exec(pathname);
    if (memberLimits !== null && (method === 'GET' || method === 'PUT')) {
      const refused = gate();
      if (refused !== undefined) return refused;
      const memberId = memberLimits[1]!;
      if (method === 'GET') return json(200, { comp: state.memberCompLimits.get(memberId) ?? null });
      const parsed = MemberLimitsSchema.safeParse(input);
      if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed');
      if (parsed.data.comp === null) state.memberCompLimits.delete(memberId);
      else state.memberCompLimits.set(memberId, parsed.data.comp);
      return json(200, parsed.data);
    }
    const staffRead = /^\/v1\/organizations\/[^/]+\/(members|invitations|roles)$/.exec(pathname);
    if (staffRead !== null && method === 'GET') {
      const refused = gate();
      if (refused !== undefined) return refused;
      const page = (data: readonly unknown[]) => ({ data, page: { cursor: null, hasMore: false, count: data.length } });
      if (staffRead[1] === 'members') return json(200, page(STAFF_MEMBERS));
      if (staffRead[1] === 'invitations') return json(200, page(STAFF_INVITATIONS));
      const noLimits = { compUnlimited: false, compMaxMinutesPerAction: null, compMaxMinutesPerShift: null, manualDiscountMaxBps: null, refundApprovalAmount: null, walletAdjustMaxAmount: null };
      return json(200, {
        data: [
          ...ORGANIZATION_ROLES.map((name) => ({
            name,
            builtIn: true,
            permissions: [...ORGANIZATION_ROLE_GRANTS[name]],
            limits:
              name === 'cashier'
                ? { compUnlimited: false, compMaxMinutesPerAction: 30, compMaxMinutesPerShift: 120, manualDiscountMaxBps: 1000, refundApprovalAmount: { amount: 50_000, currency: 'INR' }, walletAdjustMaxAmount: null }
                : noLimits,
          })),
          ...state.customRoles.map((role) => ({ name: role.name, builtIn: false, permissions: role.permissions, limits: noLimits })),
        ],
      });
    }

    // Create a custom role (F-IAM-11, `members:roles`): a name and its permission set. A name a built-in
    // or another custom role already uses is a conflict; `role_in_use` (deletion) is exercised via `staff`.
    const rolesCollection = /^\/v1\/organizations\/[^/]+\/roles$/.exec(pathname);
    if (rolesCollection !== null && method === 'POST') {
      const refused = gate();
      if (refused !== undefined) return refused;
      const body = input as { name?: string; permissions?: string[] };
      const name = String(body.name ?? '');
      if (ORGANIZATION_ROLES.includes(name as (typeof ORGANIZATION_ROLES)[number]) || state.customRoles.some((role) => role.name === name)) {
        return problem(409, 'conflict', 'A role with that name already exists.');
      }
      const role = { name, permissions: [...(body.permissions ?? [])] };
      state.customRoles.push(role);
      return json(201, { ...role, builtIn: false, limits: { compUnlimited: false, compMaxMinutesPerAction: null, compMaxMinutesPerShift: null, manualDiscountMaxBps: null, refundApprovalAmount: null, walletAdjustMaxAmount: null } });
    }

    // Edit or delete a custom role (F-IAM-11). A built-in role, or a name reserved as one, is refused; a
    // custom role named `in_use_role` cannot be deleted, to exercise the 409 a held role answers with.
    const roleItem = /^\/v1\/organizations\/[^/]+\/roles\/([a-z_]+)$/.exec(pathname);
    if (roleItem !== null && (method === 'PUT' || method === 'DELETE')) {
      const refused = gate();
      if (refused !== undefined) return refused;
      const name = roleItem[1] ?? '';
      const custom = state.customRoles.find((role) => role.name === name);
      if (custom === undefined) return problem(404, 'not_found', 'No such custom role.');
      if (method === 'PUT') {
        const body = input as { permissions?: string[] };
        custom.permissions = [...(body.permissions ?? [])];
        return json(200, { name, builtIn: false, permissions: custom.permissions, limits: { compUnlimited: false, compMaxMinutesPerAction: null, compMaxMinutesPerShift: null, manualDiscountMaxBps: null, refundApprovalAmount: null, walletAdjustMaxAmount: null } });
      }
      if (name === 'in_use_role') return problem(409, 'conflict', 'A member still holds this role.');
      state.customRoles = state.customRoles.filter((role) => role.name !== name);
      return json(204, undefined);
    }

    // Accepting a staff invitation (F-IAM-08, spec/07 §6.5): `POST /v1/invitations/accept`, not under
    // `/v1/organizations/` — the caller is not a member of the invitation's organisation yet, so it is
    // gated only on being signed in, never on `gate()`'s permissions.
    // What an invitation link is, before signing in (ADR-0046): no session needed, the token in the body.
    if (pathname === '/v1/invitations/preview' && method === 'POST') {
      const preview = INVITATION_PREVIEWS[String(input['token'] ?? '')];
      return preview === undefined ? problem(404, 'not_found', 'No such invitation.') : json(200, preview);
    }
    if (pathname === '/v1/invitations/accept' && method === 'POST') {
      if (state.session === null) return problem(401, 'unauthenticated', 'Not signed in');
      // A role that needs a second factor: refused until the session has one, then accepted like any other.
      const token = input['token'] === INVITATION_TOKENS.secondFactor && state.session.session.secondFactor != null ? INVITATION_TOKENS.valid : input['token'];
      switch (token === INVITATION_TOKENS.owner || token === INVITATION_TOKENS.ownerPhone ? INVITATION_TOKENS.valid : token) {
        case INVITATION_TOKENS.unavailable:
          return problem(409, 'invitation_unavailable', 'Invitation used, revoked or expired');
        case INVITATION_TOKENS.contactMismatch:
          return problem(403, 'invitation_contact_mismatch', 'Invitation is for someone else');
        case INVITATION_TOKENS.alreadyMember:
          return problem(409, 'already_a_member', 'Already a member');
        case INVITATION_TOKENS.secondFactor:
          return problem(403, 'second_factor_required', 'Second factor required');
        case INVITATION_TOKENS.sharedDevice:
          return problem(403, 'shared_device', 'Not on a shared counter device');
        case INVITATION_TOKENS.valid:
          return json(200, {
            organizationId: ORGANIZATION.id,
            member: {
              id: 'mem_01k5znw4hmfay8vvr14d2pf2dz',
              organizationId: ORGANIZATION.id,
              userId: state.session.user.id,
              role: 'cashier',
              venueIds: [],
              status: 'active',
              createdAt: NOW,
              updatedAt: NOW,
            },
          });
        default:
          return problem(404, 'not_found', 'No such invitation.');
      }
    }

    // HQ (spec/08 §4.16): a platform role and a second factor, or 401/403 — `platformScope()`.
    if (pathname.startsWith('/v1/hq/')) {
      if (state.session === null) return problem(401, 'unauthenticated', 'Not signed in');
      if (state.platformRole === null) return problem(403, 'forbidden', 'Not allowed');
      if (state.session.session.secondFactor == null) return problem(403, 'second_factor_required', 'Second factor required');
      const allowed: ReadonlySet<string> =
        state.platformServerPermissions === undefined ? permissionsForPlatformRole(state.platformRole) : new Set(state.platformServerPermissions);
      // Players and the audit log answer their own paths, each with its own permission (fake-hq-players-audit.ts).
      const answered = playersAndAudit.handle(method, pathname, searchParams, input, allowed, state.session.user.id);
      if (answered !== undefined) return answered;
      const paid = hqPayments.handle({ json, problem }, method, pathname, input, allowed);
      if (paid !== undefined) return paid;
      const addon = hqAddons.handle({ json, problem }, method, pathname, input, allowed);
      if (addon !== undefined) return addon;
      if (!allowed.has(hqPermissionFor(pathname))) return problem(403, 'forbidden', 'Not allowed');
      // HQ Live (F-ANL-02): the worker's read model — a busy moment across two organisations.
      if (method === 'GET' && pathname === '/v1/hq/live') return json(200, HQ_LIVE);
      // Where organisations stand in their setup (spec/14 §3.2, F-HQ-03): this console's organisation, and Nitro with a
      // venue still waiting on a PC.
      if (method === 'GET' && pathname === '/v1/hq/setup') {
        const ids = (searchParams.get('organizationId') ?? '').split(',').filter((id) => id !== '');
        if (ids.length === 0 || ids.length > 100) return problem(400, 'validation_failed', 'Validation failed');
        const nitroVenue = venueFixture({ id: NITRO_VENUE_ID, organizationId: NITRO.id, name: 'Nitro Koramangala', slug: 'nitro-koramangala', status: 'setup' });
        const data = ids.map((id) =>
          id === ORGANIZATION.id
            ? hqSetupOf(ORGANIZATION.id, [state.venue], options.setup ?? {}, state.setup, state.businessDetails)
            : id === NITRO.id
              ? hqSetupOf(NITRO.id, [nitroVenue], { stationPaired: false }, state.setup)
              : // Any other organisation HQ knows: no venue yet.
                hqSetupOf(id, [], {}, state.setup),
        );
        return json(200, { data });
      }
      return (
        directory.handle({ json, problem }, method, pathname, searchParams, input) ??
        catalogue.handle({ json, problem }, method, pathname, searchParams, input) ??
        releases.handle({ json, problem }, method, pathname, input) ??
        providers.handle({ json, problem }, method, pathname) ??
        leads.handle({ json, problem }, method, pathname, searchParams, input) ??
        migrations.handle({ json, problem }, method, pathname, searchParams, input) ??
        hq(method, pathname, headers, input)
      );
    }

    // Console routes, behind the gate.
    const venuePath = `/v1/organizations/${ORGANIZATION.id}/venues/${VENUE_ID}`;
    const organizationPath = `/v1/organizations/${ORGANIZATION.id}`;
    if (pathname.startsWith('/v1/organizations/')) {
      const refused = gate();
      if (refused !== undefined) return refused;
    }

    // The organisation's own audit log and its export (fake-hq-players-audit.ts), on `audit:read`.
    const audited = playersAndAudit.handleOrganization(ORGANIZATION.id, method, pathname, searchParams, input, new Set(state.serverPermissions));
    if (audited !== undefined) return audited;

    // Players moved from a previous system: the venue's read-only "Moved" list (fake-hq-migrations.ts), on `players:read`.
    const moved = migrations.handleOrganization(method, pathname, searchParams, new Set(state.serverPermissions), json, problem, input);
    if (moved !== undefined) return moved;

    // The Tax and invoices group's tax registration, at any venue (fake-venue-tax.ts).
    const taxed = tax.handle(method, pathname, searchParams, input);
    if (taxed !== undefined) return taxed;

    // The organisation's business details (ADR-0046): the owner's, on `settings:all`; only what changed is sent.
    if (pathname === `${organizationPath}/business-details` && (method === 'GET' || method === 'PATCH')) {
      if (!state.serverPermissions.includes('settings:all')) return problem(403, 'forbidden', 'Not allowed');
      if (method === 'PATCH') {
        const parsed = UpdateOrganizationBusinessDetailsSchema.safeParse(input);
        if (!parsed.success) {
          return problem(400, 'validation_failed', 'Validation failed', {
            errors: parsed.error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
          });
        }
        const change = parsed.data;
        state.businessDetails = {
          ...state.businessDetails,
          ...(change.legalName === undefined ? {} : { legalName: change.legalName }),
          ...(change.pan === undefined ? {} : { pan: change.pan }),
          ...(change.registeredAddress === undefined ? {} : { registeredAddress: change.registeredAddress }),
        };
      }
      const details = state.businessDetails;
      return json(200, { ...details, complete: details.legalName !== null && details.registeredAddress !== null });
    }

    // The venue's rate card (fake-venue-rate-card.ts; F-PRC-10).
    const carded = rateCard.handle(method, pathname, input);
    if (carded !== undefined) return carded;

    // Promotions, discount codes and coupon batches (fake-org-promotions.ts), at the organisation.
    const promoted = promotions.handle(method, pathname, searchParams, input);
    if (promoted !== undefined) return promoted;

    // Printers, printing and the cash drawer (fake-venue-printing.ts): before the commerce fake, which reads sales.
    // Stock (fake-venue-inventory.ts): every `…/venues/{v}/inventory/…` path, at either of the member's venues.
    const stocked = inventory.handle(method, pathname, searchParams, input, headers);
    if (stocked !== undefined) return stocked;

    const printed = printing.handle(method, pathname, searchParams, input);
    if (printed !== undefined) return printed;

    // What the organisation pays for add-ons, read-only for an owner (F-BOOT-17): `org:billing`.
    if (pathname === `/v1/organizations/${ORGANIZATION.id}/addons` && method === 'GET') {
      const denied = gate();
      if (denied !== undefined) return denied;
      if (!state.serverPermissions.includes('org:billing')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, hqAddons.forOrganization(ORGANIZATION.id));
    }

    // Clavian Boot (fake-venue-boot.ts): every `…/venues/{v}/boot/…` path.
    const booted = boot?.handle(method, pathname, searchParams, input, headers);
    if (booted !== undefined) return booted;

    // Guest passes (fake-venue-guests.ts): the venue's list, and Void of a guest's pass.
    const guested = guests.handle(method, pathname, searchParams, input);
    if (guested !== undefined) return guested;

    // A venue in full swing (`liveVenue`): each fake owns its paths and answers before the minimal ones below.
    for (const fake of liveVenue) {
      const answered = fake.handle(method, pathname, searchParams, input);
      if (answered !== undefined) return answered;
    }

    // A minimal Players fake — one player, its profile (spec/13 §8) — enough for a player's Audit tab.
    const playersPath = `/v1/organizations/${ORGANIZATION.id}/players`;
    if (pathname === playersPath && method === 'GET') {
      if (!state.serverPermissions.includes('players:read')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, { data: [ORG_PLAYER], page: { cursor: null, hasMore: false, count: 1 } });
    }
    if (pathname === `${playersPath}/${ORG_PLAYER.id}` && method === 'GET') {
      if (!state.serverPermissions.includes('players:read')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, { ...ORG_PLAYER, notes: [] });
    }

    // The member's own account (fake-account.ts): signed-in devices, the staff PIN, notification
    // preferences and the bell's inbox (empty unless a test adds rows). No permission: all about the caller.
    const mine = account.handle(method, pathname, headers, input);
    if (mine !== undefined) return mine;

    // The organisation's venues and its franchisees' (fake-org-venues.ts), each on its own permission.
    const venuesAnswer = orgVenues.handle(method, pathname, headers, input);
    if (venuesAnswer !== undefined) return venuesAnswer;

    if (pathname === venuePath && method === 'GET') return json(200, state.venue, { etag: venueEtag() });
    // A pass catalogue outside `liveVenue` (`passes`): read with `passes:read`, made with `passes:manage`.
    if (options.passes !== undefined && options.liveVenue !== true && pathname === `${organizationPath}/passes/products`) {
      if (method === 'GET') {
        if (!state.serverPermissions.includes('passes:read')) return problem(403, 'forbidden', 'Not allowed');
        return json(200, { data: state.passes, page: { cursor: null, hasMore: false, count: state.passes.length } });
      }
      if (method === 'POST') {
        if (!state.serverPermissions.includes('passes:manage')) return problem(403, 'forbidden', 'Not allowed');
        const parsed = CreatePassProductRequestSchema.safeParse(input);
        if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed');
        const { active, sortOrder, accentToken, ...terms } = parsed.data;
        const id = `ppr_01k5znw4hmfay8vvr14d2ps${String(state.passes.length).padStart(3, '0')}`;
        const created = {
          id,
          version: { ...terms, id: `ppv_01k5znw4hmfay8vvr14d2ps${String(state.passes.length).padStart(3, '0')}`, productId: id, version: 1, createdAt: NOW },
          active,
          sortOrder,
          accentToken: accentToken ?? null,
          soldToday: 0,
          createdAt: NOW,
          updatedAt: NOW,
        } as PassProduct;
        state.passes.push(created);
        return json(201, created);
      }
    }
    // Guided setup (spec/13 §13.1, F-HQ-03): read with `settings:read`, skip a venue's step with `venues:update` and the
    // organisation's own (its business details) with `settings:all`.
    /** The seeded venue's setup facts: the test's, with How players pay read from the pass catalogue when there is one. */
    const seededVenueFacts = (): SetupFacts => {
      const facts = options.setup ?? {};
      if (options.passes === undefined || facts.price !== undefined) return facts;
      return { ...facts, price: state.passes.some((pass) => passOnSaleAt(pass, state.venue.id)) };
    };
    const knownVenues = (): Venue[] => [
      ...(options.noVenues ? [] : [state.venue, ...(options.venues === 'two' ? [state.second] : [])]),
      ...orgVenues.state.created.filter((one) => !orgVenues.state.archived.has(one.id)),
    ];
    if (pathname === `${organizationPath}/setup` && method === 'GET') {
      if (!state.serverPermissions.includes('settings:read')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, organizationSetupOf(ORGANIZATION.id, knownVenues(), seededVenueFacts(), state.setup, state.businessDetails));
    }
    const organizationSkipMatch = new RegExp(`^${organizationPath}/setup/skips/([A-Za-z]+)$`).exec(pathname);
    if (organizationSkipMatch !== null && (method === 'PUT' || method === 'DELETE')) {
      if (!state.serverPermissions.includes('settings:all')) return problem(403, 'forbidden', 'Not allowed');
      if (!applyOrganizationSkip(state.setup, organizationSkipMatch[1] as string, method === 'PUT')) return problem(400, 'validation_failed', 'Validation failed');
      return json(200, organizationSetupOf(ORGANIZATION.id, knownVenues(), seededVenueFacts(), state.setup, state.businessDetails));
    }
    const venueSetupMatch = new RegExp(`^${organizationPath}/venues/(ven_[0-9a-z]+)/setup$`).exec(pathname);
    if (venueSetupMatch !== null && method === 'GET') {
      if (!state.serverPermissions.includes('settings:read')) return problem(403, 'forbidden', 'Not allowed');
      const venue = knownVenues().find((one) => one.id === venueSetupMatch[1]);
      if (venue === undefined) return problem(404, 'not_found', 'No such venue in this organisation.');
      // The seeded venue follows the test's facts; the second, which is live, has its essentials; one made here has none.
      const facts =
        venue.id === state.venue.id
          ? seededVenueFacts()
          : venue.id === state.second.id
            ? { ...(options.setup ?? {}), stationPaired: true, price: true }
            : { ...(options.setup ?? {}), stationPaired: false, price: false };
      return json(200, venueSetupOf(venue, facts, state.setup));
    }
    const skipMatch = new RegExp(`^${venuePath}/setup/skips/([A-Za-z]+)$`).exec(pathname);
    if (skipMatch !== null && (method === 'PUT' || method === 'DELETE')) {
      if (!state.serverPermissions.includes('venues:update')) return problem(403, 'forbidden', 'Not allowed');
      if (!applySkip(state.setup, state.venue.id, skipMatch[1] as string, method === 'PUT')) return problem(400, 'validation_failed', 'Validation failed');
      return json(200, venueSetupOf(state.venue, seededVenueFacts(), state.setup));
    }
    const secondPath = `/v1/organizations/${ORGANIZATION.id}/venues/${SECOND_VENUE.id}`;
    if (pathname === secondPath && method === 'GET') return json(200, state.second, { etag: '"venue-1"' });
    if (pathname === `${secondPath}/settings` && method === 'GET') return json(200, view('venue'), { etag: settingsEtag() });
    if (pathname === venuePath && method === 'PATCH') {
      if (!state.serverPermissions.includes('venues:update')) return problem(403, 'forbidden', 'Not allowed');
      const ifMatch = headers['if-match'];
      if (ifMatch === undefined) return problem(400, 'validation_failed', 'If-Match required');
      if (ifMatch !== venueEtag()) return problem(412, 'stale_write', 'Stale write', {}, { etag: venueEtag() });
      if (typeof input['name'] === 'string' && input['name'].trim() === '') {
        return problem(400, 'validation_failed', 'Invalid', { errors: [{ field: 'name', code: 'too_small', message: 'Too small: expected at least 1 character.' }] });
      }
      // The status moves the server allows (apps/server/src/modules/org/venues.ts): setup → live, live ⇄ paused.
      const nextStatus = input['status'];
      if (typeof nextStatus === 'string' && nextStatus !== state.venue.status && !(VENUE_TRANSITIONS[state.venue.status] ?? []).includes(nextStatus)) {
        return problem(409, 'conflict', `A venue cannot go from ${state.venue.status} to ${nextStatus}.`);
      }
      // ADR-0047: setup → live waits on the essentials, judged as the edit would leave the venue.
      if (nextStatus === 'live' && state.venue.status === 'setup') {
        const missing = goLiveMissing({ ...state.venue, ...(input as Partial<Venue>), status: 'setup' }, seededVenueFacts());
        if (missing.length > 0) {
          return problem(409, 'venue_not_ready', 'Venue not ready to go live', { missingSteps: missing as Problem['missingSteps'] });
        }
      }
      const { photos, logoFileId, ...rest } = input as { photos?: { cover: string | null; gallery: string[] }; logoFileId?: string | null };
      const next: Venue = { ...state.venue, ...(rest as Partial<Venue>), updatedAt: NOW };
      // ADR-0029: a trading venue's cutoff change takes effect from the next business day (the fake's today is 26 Sep).
      const cutoff = (rest as Partial<Venue>).businessDayCutoff;
      if (cutoff !== undefined && cutoff !== state.venue.businessDayCutoff && state.venue.status !== 'setup') {
        next.businessDayCutoffHistory = [{ until: '2026-09-26', cutoff: state.venue.businessDayCutoff }];
      }
      const resolve = (fileId: string) => {
        const file = state.files.get(fileId);
        return { fileId, url: file?.url ?? `https://cdn.test/${fileId}`, variants: file?.variants ?? [] };
      };
      if (photos !== undefined) {
        next.photos = { cover: photos.cover === null ? null : resolve(photos.cover), gallery: photos.gallery.map(resolve) };
      }
      // The venue's logo (F-ORG-01): a `venue_logo` upload, attached by id, read back as its URLs.
      if (logoFileId !== undefined) next.logo = logoFileId === null ? null : resolve(logoFileId);
      state.venue = next;
      state.venueVersion += 1;
      return json(200, state.venue, { etag: venueEtag() });
    }

    for (const [path, screen] of [[`${venuePath}/settings`, 'venue'], [`${organizationPath}/settings`, 'organization']] as const) {
      if (pathname !== path) continue;
      if (!state.serverPermissions.includes('settings:read')) return problem(403, 'forbidden', 'Not allowed');
      if (method === 'GET') return json(200, view(screen), { etag: settingsEtag() });
      if (method === 'PUT') {
        const ifMatch = headers['if-match'];
        if (ifMatch === undefined) return problem(400, 'validation_failed', 'If-Match required');
        if (ifMatch !== settingsEtag()) return problem(412, 'stale_write', 'Stale write', {}, { etag: settingsEtag() });
        const changes = (input['changes'] ?? []) as { key: string; value?: unknown; reset?: boolean; stationClassId?: string }[];
        for (const change of changes) {
          const definition = registry[change.key];
          if (change.stationClassId !== undefined && (screen !== 'venue' || definition?.scopes.includes('stationClass') !== true)) {
            return problem(400, 'validation_failed', 'Not a class setting');
          }
          if (definition === undefined) return problem(400, 'validation_failed', 'Unknown setting');
          if (state.values.platform[change.key]?.locked) return problem(403, 'setting_locked', 'Setting locked');
          if (!state.serverPermissions.includes(definition.editPermission)) return problem(403, 'forbidden', 'Not allowed');
          // The registry's own schema, as the server checks it: a money setting takes integer paise only.
          if (!change.reset && !definition.type.safeParse(change.value).success) return problem(400, 'validation_failed', 'Invalid value');
        }
        for (const change of changes) {
          if (change.stationClassId !== undefined) {
            const values = (state.values.stationClass[change.stationClassId] ??= {});
            if (change.reset) delete values[change.key];
            else values[change.key] = { value: change.value };
            continue;
          }
          if (change.reset) delete state.values[screen][change.key];
          else state.values[screen][change.key] = { value: change.value };
        }
        state.settingsVersion += 1;
        return json(200, view(screen), { etag: settingsEtag() });
      }
    }

    // files (spec/08 §4.19)
    if (pathname === `${organizationPath}/files` && method === 'POST') {
      if (!state.serverPermissions.includes('venues:update')) return problem(403, 'forbidden', 'Not allowed');
      const id = `fil_01k5znw4hmfay8vvr14d2pf${String(state.files.size).padStart(3, '0')}`;
      const purpose = (input['purpose'] ?? 'venue_photo') as FileResource['purpose'];
      const file: FileResource = {
        id,
        purpose,
        venueId: purpose === 'product_image' ? null : VENUE_ID,
        contentType: input['contentType'] as FileResource['contentType'],
        bytes: Number(input['bytes']),
        status: 'pending',
        url: `https://cdn.test/${id}.webp`,
        variants: [],
        createdAt: NOW,
      };
      state.files.set(id, file);
      return json(201, {
        file,
        upload: { method: 'PUT', url: `https://storage.test/public-media/${id}`, contentType: file.contentType, expiresAt: NOW },
      });
    }
    const fileMatch = new RegExp(`^${organizationPath}/files/(fil_[0-9a-z]+)(/complete)?$`).exec(pathname);
    if (fileMatch !== null) {
      const file = state.files.get(fileMatch[1] ?? '');
      if (file === undefined) return problem(404, 'not_found', 'Not found');
      if (fileMatch[2] === '/complete' && method === 'POST') {
        const uploaded = { ...file, status: 'uploaded' as const };
        state.files.set(file.id, uploaded);
        return json(200, uploaded);
      }
      if (method === 'GET') {
        const ready = { ...file, status: 'ready' as const, variants: [{ width: 320, url: `https://cdn.test/${file.id}-320.webp` }] };
        state.files.set(file.id, ready);
        return json(200, ready);
      }
    }

    // Stations, and the classes and zones the add form fills from (F-ORG-03; #408, #66).
    const page = <Row,>(data: readonly Row[]) => ({ data, page: { cursor: null, hasMore: false, count: data.length } });
    const fakeClass = {
      id: CLASS_ID,
      organizationId: ORGANIZATION.id,
      venueId: VENUE_ID,
      name: 'Standard PC',
      colorToken: 'accent-ink',
      description: 'Our everyday gaming PCs.',
      ageLimit: null,
      pricingMode: 'per_player',
      defaultCapacity: 1,
      createdAt: NOW,
      updatedAt: NOW,
    };
    const fakeZone = {
      id: 'zon_01k5znw4hmfay8vvr14d2pf3bb',
      organizationId: ORGANIZATION.id,
      venueId: VENUE_ID,
      name: 'Main floor',
      colorToken: 'warn-ink',
      floor: 0,
      createdAt: NOW,
      updatedAt: NOW,
    };
    const isPaired = (stationId: string) => state.pairedStations.has(stationId);
    // A station shows a bound device (and the `locked` mode) once a pairing has been approved for it.
    const fakeStation = {
      id: STATION_ID,
      organizationId: ORGANIZATION.id,
      venueId: VENUE_ID,
      number: 1,
      name: 'PC-01',
      kind: 'pc',
      role: 'station',
      managed: true,
      classId: fakeClass.id,
      zoneId: fakeZone.id,
      capacity: 1,
      status: 'active',
      disabledReason: null,
      mode: isPaired(STATION_ID) ? 'locked' : 'pairing',
      deviceId: isPaired(STATION_ID) ? DEVICE_ID : null,
      createdAt: NOW,
      updatedAt: NOW,
      // Edits made through `PATCH …/stations/{id}` are layered over the base, so a re-read shows them.
      ...state.stationEdits,
    };
    if (pathname === `${venuePath}/station-classes` && method === 'GET') {
      if (!state.serverPermissions.includes('stations:read')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, page([...(options.noStationClasses === true ? [] : [fakeClass]), ...state.stationClasses]));
    }
    if (pathname === `${venuePath}/station-classes` && method === 'POST') {
      if (!state.serverPermissions.includes('stations:manage')) return problem(403, 'forbidden', 'Not allowed');
      const body = input as Record<string, unknown>;
      const created = {
        ...fakeClass,
        id: `scl_${String(state.stationClasses.length + 1).padStart(26, '0')}`,
        name: body['name'],
        colorToken: body['colorToken'],
        description: (body['description'] as string | undefined) ?? '',
        ageLimit: (body['ageLimit'] as number | null | undefined) ?? null,
        pricingMode: body['pricingMode'],
        defaultCapacity: (body['defaultCapacity'] as number | undefined) ?? 1,
      };
      state.stationClasses.push(created);
      return json(201, created);
    }
    const classMatch = new RegExp(`^${venuePath}/station-classes/(scl_[0-9a-z]+)$`).exec(pathname);
    if (classMatch !== null && method === 'PATCH') {
      if (!state.serverPermissions.includes('stations:manage')) return problem(403, 'forbidden', 'Not allowed');
      const all = [fakeClass, ...state.stationClasses];
      const current = all.find((klass) => klass['id'] === classMatch[1]);
      if (current === undefined) return problem(404, 'not_found', 'No such class');
      return json(200, { ...current, ...(input as Record<string, unknown>) });
    }
    if (pathname === `${venuePath}/zones` && method === 'GET') {
      if (!state.serverPermissions.includes('floor:read')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, page([fakeZone, ...state.zones]));
    }
    if (pathname === `${venuePath}/zones` && method === 'POST') {
      if (!state.serverPermissions.includes('floor:arrange')) return problem(403, 'forbidden', 'Not allowed');
      const body = input as Record<string, unknown>;
      const created = {
        ...fakeZone,
        id: `zon_${String(state.zones.length + 1).padStart(26, '0')}`,
        name: body['name'],
        colorToken: body['colorToken'],
        floor: (body['floor'] as number | undefined) ?? 0,
      };
      state.zones.push(created);
      return json(201, created);
    }
    const zoneMatch = new RegExp(`^${venuePath}/zones/(zon_[0-9a-z]+)$`).exec(pathname);
    if (zoneMatch !== null && method === 'PATCH') {
      if (!state.serverPermissions.includes('floor:arrange')) return problem(403, 'forbidden', 'Not allowed');
      const all = [fakeZone, ...state.zones];
      const current = all.find((zone) => zone['id'] === zoneMatch[1]);
      if (current === undefined) return problem(404, 'not_found', 'No such zone');
      return json(200, { ...current, ...(input as Record<string, unknown>) });
    }
    if (pathname === `${venuePath}/stations` && method === 'GET') {
      if (!state.serverPermissions.includes('stations:read')) return problem(403, 'forbidden', 'Not allowed');
      const counter = { ...fakeStation, id: COUNTER_STATION_ID, number: 90, name: 'Front desk', role: 'counter', mode: 'desk' };
      const listed = state.removedStations.has(fakeStation.id) ? [] : [fakeStation];
      return json(200, page(options.counterStation === true ? [...listed, counter] : listed));
    }
    // Unpair the PC from a station and remove a station (ADR-0057): `stations:manage`.
    const changeMatch = new RegExp(`^${venuePath}/stations/(stn_[0-9a-z]+)/(unpair|remove)$`).exec(pathname);
    if (changeMatch !== null && method === 'POST' && changeMatch[1] === fakeStation.id) {
      if (!state.serverPermissions.includes('stations:manage')) return problem(403, 'forbidden', 'Not allowed');
      if (state.removedStations.has(fakeStation.id)) return problem(404, 'not_found', 'No such station at this venue.');
      const wasPaired = isPaired(fakeStation.id);
      if (changeMatch[2] === 'unpair') {
        if (!wasPaired) return problem(409, 'conflict', 'No PC is paired to this station.');
        state.pairedStations.delete(fakeStation.id);
        return json(200, { ...fakeStation, mode: 'pairing', deviceId: null });
      }
      state.pairedStations.delete(fakeStation.id);
      state.removedStations.add(fakeStation.id);
      return json(200, { id: fakeStation.id, number: fakeStation.number, name: fakeStation.name, removedAt: NOW, unpaired: wasPaired });
    }

    // The floor plan (F-STN-19): GET reads one level; PUT needs `If-Match` with the version read, else 412.
    if (pathname === `${venuePath}/floor-plan`) {
      const floorLevel = Number(searchParams.get('floor') ?? '0');
      const planOf = (level: number): Record<string, unknown> =>
        state.floorPlans.get(level) ?? {
          venueId: VENUE_ID,
          floor: level,
          columns: 24,
          rows: 16,
          zones: [],
          fixtures: [],
          stations: [],
          stationsElsewhere: [],
          floorsInUse: [0],
          version: 0,
          updatedAt: null,
        };
      if (method === 'GET') {
        if (!state.serverPermissions.includes('floor:read')) return problem(403, 'forbidden', 'Not allowed');
        const current = planOf(floorLevel);
        return json(200, current, { etag: `"${String(current['version'])}"` });
      }
      if (method === 'PUT') {
        if (!state.serverPermissions.includes('floor:arrange')) return problem(403, 'forbidden', 'Not allowed');
        const current = planOf(floorLevel);
        const ifMatch = headers['if-match'];
        if (ifMatch !== `"${String(current['version'])}"`) return problem(412, 'stale_write', 'Someone else saved this floor.');
        const body = input as Record<string, unknown>;
        const saved = { ...current, ...body, version: Number(current['version']) + 1, updatedAt: NOW };
        state.floorPlans.set(floorLevel, saved);
        return json(200, saved, { etag: `"${String(saved['version'])}"` });
      }
    }

    // The live floor snapshot (F-STN-14): the station with its live state from presence (spec/09 §4).
    if (pathname === `${venuePath}/floor` && method === 'GET') {
      if (!state.serverPermissions.includes('floor:read')) return problem(403, 'forbidden', 'Not allowed');
      const online = isPaired(fakeStation.id);
      return json(200, {
        version: 0,
        stations: state.removedStations.has(fakeStation.id) ? [] : [
          {
            stationId: fakeStation.id,
            number: fakeStation.number,
            name: fakeStation.name,
            kind: fakeStation.kind,
            classId: fakeStation.classId,
            zoneId: fakeStation.zoneId,
            capacity: fakeStation.capacity,
            status: fakeStation.status,
            disabledReason: fakeStation.disabledReason,
            managed: true,
            online,
            mode: online ? (options.stationSetup !== undefined ? 'setting_up' : fakeStation.mode) : null,
            lastSeenAt: online ? NOW : null,
            health: { openAlerts: 0, severity: null, kinds: [] },
            // What it is running now (F-CAT-04): nothing unless a test sets `stationTitle`, and nothing offline.
            title: online ? (options.stationTitle ?? null) : null,
            // Its start-up setup (ADR-0055), when a test puts it in one.
            setup:
              online && options.stationSetup !== undefined && options.stationSetup !== null
                ? { ...options.stationSetup, startedAt: NOW, endsBy: NOW }
                : null,
          },
        ],
      });
    }
    // "Needs attention" (F-STN-15): nothing open on the one station.
    if (pathname === `${venuePath}/needs-attention` && method === 'GET') {
      if (!state.serverPermissions.includes('floor:read')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, { alerts: [], commands: [] });
    }

    // The station drawer's live-ops (F-STN-16 … F-STN-18): each answers `202` with the command view.
    const commandView = (type: string) => ({
      id: 'cmd_01k5znw4hmfay8vvr14d2pf3ee',
      stationId: fakeStation.id,
      type,
      status: 'queued' as const,
      createdAt: NOW,
      expiresAt: NOW,
    });
    const drawerAction = (action: 'power' | 'lock' | 'maintenance' | 'message' | 'setup/skip', permission: string, type: string): FakeResponse | undefined => {
      const match = new RegExp(`^${venuePath}/stations/([^/]+)/${action}$`).exec(pathname);
      if (match === null || method !== 'POST') return undefined;
      if (!state.serverPermissions.includes(permission)) return problem(403, 'forbidden', 'Not allowed');
      return json(202, commandView(type));
    };
    const power = drawerAction('power', 'stations:power', 'station.power');
    if (power !== undefined) return power;
    const lockAction = drawerAction('lock', 'sessions:lock', 'session.lock');
    if (lockAction !== undefined) return lockAction;
    const maintenance = drawerAction('maintenance', 'stations:maintenance', 'station.mode.set');
    if (maintenance !== undefined) return maintenance;
    const messageAction = drawerAction('message', 'stations:message', 'session.message');
    if (messageAction !== undefined) return messageAction;
    // Skip setup (ADR-0055): `stations:maintenance`, and only while the station is setting up (else `409`).
    if (new RegExp(`^${venuePath}/stations/([^/]+)/setup/skip$`).test(pathname) && method === 'POST' && options.stationSetup === undefined) {
      return problem(409, 'conflict', 'This PC is not setting up.');
    }
    const skipSetup = drawerAction('setup/skip', 'stations:maintenance', 'station.setup.skip');
    if (skipSetup !== undefined) return skipSetup;

    // An offline code for a station (F-SES-24, spec/05 §8.4): `sessions:offline_code`. A fixed code,
    // valid to the end of the next 10-minute step. Station 999's id has no offline secret (`409`).
    const offlineCodeMatch = new RegExp(`^${venuePath}/stations/([^/]+)/offline-codes$`).exec(pathname);
    if (offlineCodeMatch !== null && method === 'POST') {
      if (!state.serverPermissions.includes('sessions:offline_code')) return problem(403, 'forbidden', 'Not allowed');
      const body = input as { action?: string; minutesBucket?: number };
      if (state.offlineSecretMissing) return problem(409, 'conflict', 'This station cannot generate offline codes.');
      return json(200, { code: '04817263', action: body.action, minutesBucket: body.minutesBucket, timeStep: 2966400, expiresAt: '2026-09-22T09:20:00.000Z' });
    }
    // The lockdown test (F-STN-21): POST sends it (`stations:manage`) and answers pending; the station's answer is the
    // next GET — one failing check, the rest held. Never tested before the first run (404).
    const lockdownMatch = new RegExp(`^${venuePath}/stations/([^/]+)/lockdown-test$`).exec(pathname);
    if (lockdownMatch !== null) {
      const stationId = lockdownMatch[1] ?? '';
      if (method === 'POST') {
        if (!state.serverPermissions.includes('stations:manage')) return problem(403, 'forbidden', 'Not allowed');
        state.lockdownTested = true;
        return json(202, { testId: '0199e830-0000-7000-8000-00000000aaaa', stationId, status: 'pending', requestedAt: '2026-10-01T10:00:00.000Z', requestedBy: null, ranAt: null, mode: null, checks: [] });
      }
      if (!state.lockdownTested) return problem(404, 'not_found', 'This station has never been tested.');
      return json(200, {
        testId: '0199e830-0000-7000-8000-00000000aaaa',
        stationId,
        status: 'failed',
        requestedAt: '2026-10-01T10:00:00.000Z',
        requestedBy: null,
        ranAt: '2026-10-01T10:00:02.000Z',
        mode: 'locked',
        checks: [
          { id: 'live.guard', name: 'Process-start guard', outcome: 'fail', detail: 'the watcher is not running (guard_down)' },
          { id: '§9.4', name: 'File dialog → cmd.exe', outcome: 'pass', detail: 'the execution guard denies cmd.exe' },
        ],
      });
    }
    // The recovery code for station-cli undo (F-LCK-06): `stations:recovery`, shown once.
    if (new RegExp(`^${venuePath}/stations/([^/]+)/recovery-code$`).test(pathname) && method === 'POST') {
      if (!state.serverPermissions.includes('stations:recovery')) return problem(403, 'forbidden', 'Not allowed');
      return json(201, { code: 'eyJhbGciOiJFZERTQSJ9.eyJhY3Rpb24iOiJ1bmRvIn0.c2ln', codeId: 'c-1', expiresAt: '2026-10-01T10:15:00.000Z' });
    }
    // Offline play (spec/05 §8.4a, §8.5): the venue's staff codes and the sessions its PCs ran offline. One staff-code
    // session waits to be settled; issuing returns the digits once; `sessions:offline_code` issues, revokes and settles.
    const offlinePlay = offlinePlayState();
    const codesPath = `${venuePath}/offline-staff-codes`;
    if (new RegExp(`^${codesPath}$`).test(pathname)) {
      if (method === 'GET') {
        if (!state.serverPermissions.includes('sessions:read')) return problem(403, 'forbidden', 'Not allowed');
        return json(200, { items: offlinePlay.codes });
      }
      if (method === 'POST') {
        if (!state.serverPermissions.includes('sessions:offline_code')) return problem(403, 'forbidden', 'Not allowed');
        const body = input as { count: number; minutes: number; validHours: number; note?: string };
        const issued = Array.from({ length: body.count }, (_, index) => ({
          id: `osc_01k5znw4hmfay8vvr14d2pf${String(100 + offlinePlay.codes.length + index).padStart(3, '0')}`,
          code: String(40_817_263 + index).padStart(8, '0'),
          minutes: body.minutes,
          expiresAt: '2026-10-04T10:00:00.000Z',
        }));
        for (const code of issued) {
          offlinePlay.codes.unshift({ id: code.id, venueId: 'ven_fake', minutes: code.minutes, expiresAt: code.expiresAt, status: 'live', issuedAt: '2026-10-01T10:00:00.000Z', issuedBy: null, note: body.note ?? null, usedAt: null, usedStationId: null });
        }
        return json(201, { codes: issued });
      }
    }
    const revokeMatch = new RegExp(`^${codesPath}/([^/]+)/revoke$`).exec(pathname);
    if (revokeMatch !== null && method === 'POST') {
      if (!state.serverPermissions.includes('sessions:offline_code')) return problem(403, 'forbidden', 'Not allowed');
      const code = offlinePlay.codes.find((entry) => entry.id === revokeMatch[1]);
      if (code === undefined) return problem(404, 'not_found', 'No such code');
      code.status = 'revoked';
      return json(200, code);
    }
    if (new RegExp(`^${venuePath}/offline-sessions$`).test(pathname) && method === 'GET') {
      if (!state.serverPermissions.includes('sessions:read')) return problem(403, 'forbidden', 'Not allowed');
      // `?status=needs_attention`: the ones staff must still settle (the Floor's "What needs you" counts them).
      const wanted = searchParams.get('status');
      return json(200, { items: wanted === null ? offlinePlay.sessions : offlinePlay.sessions.filter((row) => row['status'] === wanted) });
    }
    const resolveMatch = new RegExp(`^${venuePath}/offline-sessions/([^/]+)/resolve$`).exec(pathname);
    if (resolveMatch !== null && method === 'POST') {
      if (!state.serverPermissions.includes('sessions:offline_code')) return problem(403, 'forbidden', 'Not allowed');
      const row = offlinePlay.sessions.find((entry) => entry.id === resolveMatch[1]);
      if (row === undefined) return problem(404, 'not_found', 'No such offline session');
      const body = input as { resolution: string; note?: string };
      Object.assign(row, { status: 'settled', resolution: body.resolution, attention: null, note: body.note ?? null, resolvedAt: '2026-10-01T11:00:00.000Z' });
      return json(200, row);
    }
    // What a station reports installed from the venue library (F-CAT-03): `library:read`. Two of three titles, one
    // of them updating, and the launchers found.
    const installedMatch = new RegExp(`^${venuePath}/stations/([^/]+)/installed$`).exec(pathname);
    if (installedMatch !== null && method === 'GET') {
      if (!state.serverPermissions.includes('library:read')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, {
        scannedAt: '2026-09-29T10:05:00.000Z',
        installedCount: 2,
        titles: [
          { entryId: 'lib_01k5znw4hmfay8vvr14d2pf001', name: 'Counter-Strike 2', installed: true, updating: null, version: '20123456', unverified: false },
          { entryId: 'lib_01k5znw4hmfay8vvr14d2pf002', name: 'Dota 2', installed: true, updating: { progress: 64 }, version: null, unverified: false },
          { entryId: 'lib_01k5znw4hmfay8vvr14d2pf003', name: 'PUBG', installed: false, updating: null, version: null, unverified: false },
          { entryId: 'lib_01k5znw4hmfay8vvr14d2pf004', name: 'Apex Legends', installed: true, updating: null, version: null, unverified: true },
          // F-CAT-09: the apps the PC installs for players, and what it said about them.
          ...(options.playersApps === true
            ? [
                {
                  entryId: 'lib_01k5znw4hmfay8vvr14d2pf005',
                  name: 'Discord',
                  installed: false,
                  updating: null,
                  version: null,
                  unverified: false,
                  installForPlayers: true,
                  provisionable: true,
                  provisioning: { state: 'failed', failure: 'signer_mismatch', detail: 'signed by Someone Else Ltd', attempts: 1, nextAttemptAt: '2026-09-29T10:25:00.000Z' },
                },
                {
                  entryId: 'lib_01k5znw4hmfay8vvr14d2pf006',
                  name: 'WhatsApp',
                  installed: false,
                  updating: null,
                  version: null,
                  unverified: false,
                  installForPlayers: false,
                  provisionable: true,
                  provisioning: { state: 'other_account_only', failure: null, detail: null, attempts: 0, nextAttemptAt: null },
                },
                {
                  // ADR-0056: an app with a recipe the PC was asked to install, waiting for the PC to be free.
                  entryId: 'lib_01k5znw4hmfay8vvr14d2pf007',
                  name: 'Spotify',
                  installed: false,
                  updating: null,
                  version: null,
                  unverified: false,
                  installForPlayers: false,
                  provisionable: true,
                  provisioning: { state: 'queued', failure: null, detail: null, attempts: 0, nextAttemptAt: null },
                },
                {
                  // An app with a recipe, Install for players off, nowhere on the PC: the station says nothing of it.
                  entryId: 'lib_01k5znw4hmfay8vvr14d2pf008',
                  name: 'Telegram',
                  installed: false,
                  updating: null,
                  version: null,
                  unverified: false,
                  installForPlayers: false,
                  provisionable: true,
                },
              ]
            : []),
        ].map((title) => ({ installForPlayers: false, provisionable: false, provisioning: null, sizeBytes: null, ...title })),
        launchers: ['steam', 'riot'],
      });
    }
    // Each PC's station client (F-STN-12): `stations:read`. The seeded station runs an older version and is offered
    // the stable rollout's target, unless the options say otherwise.
    if (pathname === `${venuePath}/station-clients` && method === 'GET') {
      if (!state.serverPermissions.includes('stations:read')) return problem(403, 'forbidden', 'Not allowed');
      const seeded: StationClient | null =
        options.stationClient === null
          ? null
          : {
              stationId: STATION_ID,
              clientVersion: '1.9.41000.0',
              kioskVersion: '1.9.7',
              targetVersion: '1.9.41234.0',
              state: 'idle',
              detail: null,
              reportedAt: '2026-09-30T08:00:00.000Z',
              ...options.stationClient,
            };
      return json(200, { channel: options.stationClientChannel ?? 'stable', data: seeded === null ? [] : [seeded] });
    }
    // Update now (F-STN-12, spec/10 §8.2): `stations:maintenance`; an offline (unpaired) station is refused `409`, as the
    // api refuses one that holds no socket. Answers the `station.client.update` command.
    const updateNowMatch = new RegExp(`^${venuePath}/stations/([^/]+)/client-update/install-now$`).exec(pathname);
    if (updateNowMatch !== null && method === 'POST') {
      if (!state.serverPermissions.includes('stations:maintenance')) return problem(403, 'forbidden', 'Not allowed');
      if (!isPaired(updateNowMatch[1] ?? '')) return problem(409, 'conflict', 'The station is offline.');
      state.updateNowRequests += 1;
      const reason = (input as { reason?: unknown } | undefined)?.reason;
      state.updateNowReasons.push(typeof reason === 'string' ? reason : 'install_now');
      return json(202, commandView('station.client.update'));
    }
    // The installers a PC is set up with (F-STN-01, F-STN-17): `stations:read`. The stable release carries both MSIs,
    // unless the options leave the Counter out (a release before it) or publish nothing.
    if (pathname === `${venuePath}/station-installers` && method === 'GET') {
      if (!state.serverPermissions.includes('stations:read')) return problem(403, 'forbidden', 'Not allowed');
      const installers = options.stationInstallers ?? 'both';
      if (installers === 'none') return json(200, { version: null, station: null, counter: null, setup: null });
      const base = 'https://cdn.clavian.test/station/releases/1.9.41234.0';
      return json(200, {
        version: '1.9.41234.0',
        station: { url: `${base}/ClavianStation-1.9.41234.0.msi`, sha256: 'a'.repeat(64), size: 94_371_840 },
        counter: installers === 'station-only' ? null : { url: `${base}/ClavianCounter-1.9.41234.0.msi`, sha256: 'b'.repeat(64), size: 94_371_841 },
        // The branded setup exe (2026-10-14); a release before it has none, and the console offers the MSIs.
        setup: installers === 'both' ? { url: `${base}/ClavianSetup-1.9.41234.0.exe`, sha256: 'c'.repeat(64), size: 205_520_896 } : null,
      });
    }
    // Whether a station will wake from Wake-on-LAN (F-STN-17): `stations:read`. Fast Startup is still on, so it needs
    // attention; its one wired adapter is armed.
    const wakeMatch = new RegExp(`^${venuePath}/stations/([^/]+)/wake-readiness$`).exec(pathname);
    if (wakeMatch !== null && method === 'GET') {
      if (!state.serverPermissions.includes('stations:read')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, {
        readiness: {
          checkedAt: '2026-10-01T08:00:00.000Z',
          fastStartupOff: false,
          adapters: [{ mac: '00:1A:2B:3C:4D:5E', name: 'Intel(R) Ethernet Connection I219-V', magicPacket: true, wakeArmed: true, powerManagementOn: true, ready: true }],
          ready: false,
          issues: ['fast_startup_on'],
          reportedAt: '2026-10-01T08:00:01.000Z',
        },
        macAddresses: ['00:1A:2B:3C:4D:5E'],
      });
    }
    // A station's health and telemetry (F-STN-11): `stations:read`. The one station has not reported yet.
    const healthMatch = new RegExp(`^${venuePath}/stations/([^/]+)/(health|telemetry)$`).exec(pathname);
    if (healthMatch !== null && method === 'GET') {
      if (!state.serverPermissions.includes('stations:read')) return problem(403, 'forbidden', 'Not allowed');
      const stationId = healthMatch[1] as string;
      return healthMatch[2] === 'health'
        ? json(200, { stationId, status: 'unknown', open: [], recent: [], latest: null })
        : json(200, { stationId, samples: [] });
    }
    // The owners' daily summary (F-ANL-07): an owner's alone, on top of `reports:financial`.
    const daily = fakeDailySummaryResponse({ pathname, method, searchParams, input, permissions: state.serverPermissions, role: state.organizationRole, organizationPath, sent: state.dailySummarySent });
    if (daily !== undefined) return daily.status === 403 ? problem(403, 'forbidden', 'Only an owner reads the daily summary.') : json(daily.status, daily.body);
    // The Reports tabs (F-ANL-03): range, revenue, sessions, products, staff — deterministic figures for any range.
    const report = fakeReportsResponse({ pathname, method, searchParams, permissions: state.serverPermissions, organizationPath, venueId: VENUE_ID });
    if (report !== undefined) return report.status === 200 ? json(200, report.body) : problem(report.status, report.status === 403 ? 'forbidden' : 'validation-failed', 'Not allowed');
    // The Reports overview (F-ANL-01): `reports:financial`. A day of split tenders and a refund, one venue's figures.
    if ((pathname === `${venuePath}/reports/overview` || pathname === `${organizationPath}/reports/overview`) && method === 'GET') {
      if (!state.serverPermissions.includes('reports:financial')) return problem(403, 'forbidden', 'Not allowed');
      const from = searchParams.get('from') ?? '2026-09-25';
      const to = searchParams.get('to') ?? '2026-10-01';
      const inr = (amount: number) => ({ amount, currency: 'INR' });
      if (pathname === `${organizationPath}/reports/overview`) {
        return json(200, {
          from,
          to,
          venues: [
            { venueId: VENUE_ID, name: 'Playground Central', timeZone: 'Asia/Kolkata', currency: 'INR', revenue: inr(70_000), sessions: 6, occupancyBasisPoints: 4200, newPlayers: 2, orders: 3 },
            { venueId: 'ven_01k5znw4hmfay8vvr14d2pf2df', name: 'Playground North', timeZone: 'Asia/Kolkata', currency: 'INR', revenue: null, sessions: null, occupancyBasisPoints: null, newPlayers: null, orders: null },
          ],
          revenue: [inr(70_000)],
          sessions: 6,
          receivablesOutstanding: inr(15_000),
        });
      }
      return json(200, {
        venueId: VENUE_ID,
        from,
        to,
        timeZone: 'Asia/Kolkata',
        businessDayCutoff: '06:00',
        businessDayCutoffHistory: [],
        currency: 'INR',
        revenue: {
          total: inr(70_000),
          payments: inr(80_000),
          refunds: inr(10_000),
          byTender: [
            { tender: 'cash', amount: inr(45_000) },
            { tender: 'counter_card', amount: inr(0) },
            { tender: 'counter_upi', amount: inr(25_000) },
            { tender: 'online', amount: inr(0) },
            { tender: 'wallet', amount: inr(0) },
          ],
        },
        revenueByDay: [
          { date: from, revenue: null },
          { date: to, revenue: inr(70_000) },
        ],
        netSales: { total: inr(70_000), discounts: inr(0), walletCredit: inr(0), byCategory: [{ category: 'other', amount: inr(70_000) }] },
        tax: null,
        deferred: { total: inr(100_000), passes: null, walletCash: inr(100_000), walletBonus: inr(0), points: null, reservationPrepayments: null },
        orders: { placed: 2, paid: 2, averageOrderValue: inr(35_000) },
        players: { active: 4, new: 1, returning: 2 },
        sessions: { sessions: 6, playSeconds: 21_600, averageSeconds: 3600 },
        occupancy: { total: { inSessionSeconds: 21_600, availableSeconds: 51_428, ratio: { numerator: 21_600, denominator: 51_428, basisPoints: 4200 } }, byClass: [], hoursSet: true },
        titles: { plays: 5, seconds: 18_000, players: 3, top: [{ titleId: 'gam_01k5znw4hmfay8vvr14d2pf5e1', name: 'Valorant', plays: 3, seconds: 10_800, players: 3 }] },
        receivables: { outstanding: inr(15_000), players: 1, days0to7: inr(15_000), days8to30: inr(0), days31plus: inr(0) },
        refundRate: { numerator: 10_000, denominator: 80_000, basisPoints: 1250 },
        comps: null,
        calls: { count: 2, medianAcknowledgeSeconds: 45, medianResolveSeconds: 300, byCategory: [] },
        cashVariance: null,
        wallet: { topUps: inr(100_000), bonusGranted: inr(0), spent: inr(0), spentCash: inr(0), refunded: inr(0), outstandingCash: inr(100_000), outstandingBonus: inr(0) },
        turnover: [{ registration: `venue:${VENUE_ID}`, taxMode: 'unregistered', turnover: inr(70_000), days: 1 }],
        notRecorded: ['M-17', 'M-18', 'M-20', 'M-21'],
        partial: false,
        rollupDays: 1,
        liveDays: 1,
      });
    }
    // The Games played report (F-CAT-04): `reports:read`. Two titles over the range, on two PCs.
    if (pathname === `${venuePath}/reports/games-played` && method === 'GET') {
      if (!state.serverPermissions.includes('reports:read')) return problem(403, 'forbidden', 'Not allowed');
      const from = searchParams.get('from') ?? '2026-09-25';
      const to = searchParams.get('to') ?? '2026-10-01';
      const valorant = 'gam_01k5znw4hmfay8vvr14d2pf5e1';
      const cs2 = 'gam_01k5znw4hmfay8vvr14d2pf5e2';
      return json(200, {
        from,
        to,
        timeZone: 'Asia/Kolkata',
        totalSeconds: 5 * 3600,
        players: 3,
        titles: [
          { titleId: valorant, name: 'Valorant', seconds: 3 * 3600, sessions: 4, players: 3 },
          { titleId: cs2, name: 'Counter-Strike 2', seconds: 2 * 3600, sessions: 2, players: 1 },
        ],
        days: [
          { date: from, seconds: 3 * 3600, titles: [{ titleId: valorant, seconds: 3 * 3600 }] },
          { date: to, seconds: 2 * 3600, titles: [{ titleId: cs2, seconds: 2 * 3600 }] },
        ],
        stations: [
          { stationId: STATION_ID, number: 7, name: null, seconds: 4 * 3600, titles: [{ titleId: valorant, seconds: 2 * 3600 }, { titleId: cs2, seconds: 2 * 3600 }] },
          { stationId: 'stn_01k5znw4hmfay8vvr14d2pf5f2', number: 4, name: 'Streamer Booth', seconds: 3600, titles: [{ titleId: valorant, seconds: 3600 }] },
        ],
      });
    }
    // Install one app on one PC from its station window (ADR-0056): `library:manage`, one `station.app.install`.
    const appInstallMatch = new RegExp(`^${venuePath}/stations/([^/]+)/apps/([^/]+)/install$`).exec(pathname);
    if (appInstallMatch !== null && method === 'POST') {
      if (!state.serverPermissions.includes('library:manage')) return problem(403, 'forbidden', 'Not allowed');
      return json(202, {
        id: 'cmd_01k5znw4hmfay8vvr14d2pi001',
        stationId: appInstallMatch[1],
        type: 'station.app.install',
        status: 'queued',
        createdAt: '2026-09-29T10:05:00.000Z',
        expiresAt: '2026-10-06T10:05:00.000Z',
      });
    }
    // A rescan asked from the station sheet (F-CAT-03): `stations:manage`, one `station.library.rescan` per station.
    if (pathname === `${venuePath}/library/rescan` && method === 'POST') {
      if (!state.serverPermissions.includes('stations:manage')) return problem(403, 'forbidden', 'Not allowed');
      const ids = ((input as { stationIds?: string[] }).stationIds ?? [STATION_ID]);
      return json(202, {
        commands: ids.map((stationId) => ({
          id: 'cmd_01k5znw4hmfay8vvr14d2pr001',
          stationId,
          type: 'station.library.rescan',
          status: 'queued',
          createdAt: '2026-09-29T10:05:00.000Z',
          expiresAt: '2026-09-29T10:10:00.000Z',
        })),
      });
    }
    if (pathname === `${venuePath}/stations` && method === 'POST') {
      if (!state.serverPermissions.includes('stations:manage')) return problem(403, 'forbidden', 'Not allowed');
      const body = input as { number?: number; name?: string; kind?: string; classId?: string; zoneId?: string; capacity?: number; managed?: boolean };
      // ADR-0033: a PC is managed (it pairs); every other kind is staff-run unless the request says otherwise.
      const managed = body.managed ?? (body.kind ?? 'pc') === 'pc';
      return json(201, {
        ...fakeStation,
        id: 'stn_01k5znw4hmfay8vvr14d2pf3dd',
        number: Number(body.number),
        name: body.name ?? null,
        kind: body.kind ?? 'pc',
        classId: body.classId ?? fakeClass.id,
        zoneId: body.zoneId ?? null,
        capacity: body.capacity ?? fakeClass.defaultCapacity,
        managed,
        mode: managed ? 'pairing' : 'locked',
        deviceId: null,
      });
    }

    // Edit the seeded station (F-ORG-03): only the fields sent change, layered over the base for the
    // next read. `number` 999 is treated as another station's number, to exercise the conflict path.
    const editMatch = new RegExp(`^${venuePath}/stations/([^/]+)$`).exec(pathname);
    if (editMatch !== null && method === 'PATCH') {
      if (!state.serverPermissions.includes('stations:manage')) return problem(403, 'forbidden', 'Not allowed');
      const patch = input as Record<string, unknown>;
      if (patch.number === 999) return problem(409, 'conflict', 'Another station at this venue already has that number.');
      state.stationEdits = { ...state.stationEdits, ...patch };
      return json(200, { ...fakeStation, ...state.stationEdits, id: editMatch[1] ?? fakeStation.id, updatedAt: NOW });
    }

    // Approve a device pairing: staff read the 8-char code off the PC and bind its device (F-STN-02,
    // spec/07 §5). `stations:manage`, org-scoped. The one live code is `PAIRING_CODE`; anything else is
    // an unknown/expired pairing (404). Approving records the station as paired so the list shows it.
    const approveMatch = new RegExp(`^${organizationPath}/device-pairings/([0-9A-Z]{8})/approve$`).exec(pathname);
    if (approveMatch !== null && method === 'POST') {
      if (!state.serverPermissions.includes('stations:manage')) return problem(403, 'forbidden', 'Not allowed');
      const code = approveMatch[1] ?? '';
      const body = input as { venueId?: string; mode?: string; stationId?: string; station?: Record<string, unknown> };
      if (code !== PAIRING_CODE) return problem(404, 'not_found', 'No pairing with that code, or it has expired.');
      const stationId = body.mode === 'existing' ? (body.stationId ?? fakeStation.id) : 'stn_01k5znw4hmfay8vvr14d2pf3dd';
      state.pairedStations.add(stationId);
      return json(200, { ...fakeStation, id: stationId, mode: 'locked', deviceId: DEVICE_ID });
    }

    // Top-ups asked for at a PC (F-KIO-10): the desk's list, and its Take payment / Cancel, on `wallet:topup`.
    const kioskTopUpsPath = `/v1/organizations/${ORGANIZATION.id}/wallet/top-up-requests`;
    if (pathname === kioskTopUpsPath && method === 'GET') {
      if (!state.serverPermissions.includes('wallet:topup')) return problem(403, 'forbidden', 'Not allowed');
      return json(200, { data: state.kioskTopUps.filter((row) => row.status === 'awaiting_desk') });
    }
    const kioskTopUpMatch = new RegExp(`^${kioskTopUpsPath}/([^/]+)/(complete|cancel)$`).exec(pathname);
    if (kioskTopUpMatch !== null && method === 'POST') {
      if (!state.serverPermissions.includes('wallet:topup')) return problem(403, 'forbidden', 'Not allowed');
      const row = state.kioskTopUps.find((candidate) => candidate.id === kioskTopUpMatch[1]);
      if (row === undefined) return problem(404, 'not_found', 'No such request.');
      if (row.status !== 'awaiting_desk') return problem(409, 'wallet_topup_closed', 'Already settled.');
      row.status = kioskTopUpMatch[2] === 'complete' ? 'applied' : 'cancelled';
      return json(200, row);
    }

    return problem(404, 'not_found', `No fake route for ${method} ${pathname}`);
  }

  /** The generated client's `fetch`. */
  async function fetch(input: string, init: { method: string; headers: Headers; body?: string }): Promise<Response> {
    const headers: Record<string, string> = {};
    init.headers.forEach((value, name) => {
      headers[name.toLowerCase()] = value;
    });
    const answer = handle(init.method, input, headers, init.body === undefined ? undefined : (JSON.parse(init.body) as unknown));
    return new Response(answer.status === 204 ? null : answer.body, { status: answer.status, headers: answer.headers });
  }

  /** The object-storage PUT a photo upload makes. */
  async function putBytes(url: string): Promise<{ ok: boolean }> {
    state.requests.push({ method: 'PUT', path: url, headers: {}, body: undefined });
    return { ok: url.startsWith('https://storage.test/') };
  }

  /** Another editor saves the venue: its version moves on, as a second console's save would. */
  function editVenueElsewhere(patch: Partial<Venue>): void {
    state.venue = { ...state.venue, ...patch };
    state.venueVersion += 1;
  }

  function editSettingElsewhere(screen: 'venue' | 'organization', key: string, value: unknown): void {
    state.values[screen][key] = { value };
    state.settingsVersion += 1;
  }

  /** The console gate starts asking this session for a second factor (spec/07 §6.6). */
  function requireSecondFactorNow(): void {
    state.gateOn = true;
    if (state.session !== null) state.session = sessionFor(false);
  }

  /** HQ locks a setting at platform scope: the value above moves, so every screen's ETag does. */
  function lockSettingElsewhere(key: string, value: unknown): void {
    state.values.platform[key] = { value, locked: true };
    state.settingsVersion += 1;
  }

  /** Another HQ editor saves the brand: a new revision, so this screen's `If-Match` is stale. */
  function editBrandElsewhere(values: BrandRuntimeValues): void {
    state.brand = { revision: state.brand.revision + 1, updatedAt: NOW, values: { ...state.brand.values, ...values } };
  }

  return {
    state,
    /** HQ's organisations and venues (`fake-hq-directory.ts`). */
    directory: directory.state,
    /** HQ's game catalogue (`fake-hq-catalogue.ts`). */
    catalogue: catalogue.state,
    /** The venue's till, passes and library (`fake-venue-ops.ts`). */
    ops: ops.state,
    /** HQ's station releases, rollouts and fleet (`fake-hq-releases.ts`). */
    releases: releases.state,
    /** HQ's provider status page (`fake-hq-providers.ts`). */
    providers: providers.state,
    /** HQ's sales pipeline: leads, timeline, templates (`fake-hq-leads.ts`). */
    leads: leads.state,
    /** HQ's online payments: the provider, each organisation's payee (`fake-hq-payments.ts`). */
    hqPayments,
    /** HQ's add-ons: the catalogue and each organisation's (`fake-hq-addons.ts`). */
    hqAddons,
    /** HQ's moves from another system and the venue's moved players (`fake-hq-migrations.ts`). */
    migrations,
    handle,
    fetch,
    putBytes,
    editVenueElsewhere,
    editSettingElsewhere,
    lockSettingElsewhere,
    editBrandElsewhere,
    requireSecondFactorNow,
    playersAndAudit,
    /** A player's on account (`fake-venue-credit.ts`), with `liveVenue`. */
    credit: credit.state,
    /** Online payments (`fake-venue-payments.ts`), with `liveVenue`: the gate, the intents, and the player's side. */
    payments,
    /** The venue's sales (`fake-venue-commerce.ts`), with `liveVenue`: e.g. seed a kiosk pass order (F-KIO-14). */
    sales: commerce,
    /** The organisation's players and their wallets (`fake-venue-players.ts`), with `liveVenue`. */
    players: players.state,
    /** The venue's printers, print jobs and drawer kicks (`fake-venue-printing.ts`), always. */
    printing: printing.state,
    /** The venue's tax registration (`fake-venue-tax.ts`), always. */
    tax: tax.state,
    rateCard: rateCard.state,
    /** Promotions, discount codes and coupon batches (`fake-org-promotions.ts`), always. */
    promotions: promotions.state,
    /** The member's own sessions, PINs, notification preferences and inbox (`fake-account.ts`). */
    account: account.state,
    /** The daily summaries an owner asked to be emailed now. */
    dailySummarySent: state.dailySummarySent,
    /** The organisation's venues made and archived here, and its franchisees (`fake-org-venues.ts`). */
    orgVenues: orgVenues.state,
    /** The venue's stock (`fake-venue-inventory.ts`), always: items, movements, counts, recipes. */
    inventory: inventory.state,
    /** The venue's guest passes (`fake-venue-guests.ts`), always; on with `guests`. */
    guests: guests.state,
    /** Clavian Boot's servers, images, PCs and the writes made (`fake-venue-boot.ts`); present with the `boot` option. */
    boot: boot?.state,
  };
}

export type FakeServer = ReturnType<typeof createFakeServer>;
